import { compileAmbient } from '../janus-engine/compile/compile-ambient';
// Shared shapes for PHP-backed arrays surfaced to the front end.
import { permanentStatusAllowed } from '../janus-engine/compile/permanent-status-guard';
//
// First used by the turn-widget carve-out (single-subsystem types.ts);
// graduated to a shared module when the encounter-summary carve-out needed
// the same EncounterArr / CampaignArr surface. Any future carve-out (e.g.
// the template.js extractions queued in [[project_template_js_refactor]])
// that reads cached encounter_arr / campaign_arr should import from here.
//
// Field membership policy:
//   • Only add a field when an in-tree carve-out actually reads it.
//   • The `[key: string]: unknown` indexer lets callers pass through the
//     full ~80-field arrays returned by get_*_arr.php without spreading
//     every field into the interface.
//   • Per-subsystem refinements (interfaces that extend these with required
//     fields the subsystem assumes) belong in that subsystem's local
//     `types.ts`, not here.


// PHP "[value]" wrapping convention. All array fields surfaced by get_*_arr.php
// come through as a single-element array even for scalars. The renderers read
// .[0] off every field, so PhpScalar models exactly that shape.
//
// `[T] | []` lets `arr.key?.[0]` produce `T | undefined` (the empty-array case
// substitutes for "field missing" the same way an empty PHP array does).
export type PhpScalar<T> = [T] | [];


// ─────────────────────────────────────────────────────────────────────────────
// [[project_classification_nest_and_walk]] P1 (2026-05-28)
// Front-end mirror of ItemLayerTypeCatalog::isDefaultProvider() +
// aggregationStrategy(). Hardcoded because (a) the set is small/stable,
// (b) we have no DB read pattern on the front-end. Keep in sync with the DB
// rows set by P2 and P4 migrations.
//
// DEFAULT_PROVIDER_LAYERS: layer slot names that act as carriers — readers
// (layerOf, layerEntriesOf, picturesOf) descend into the nested arr at
// arr.item_layer_types[<name>] to resolve missing host keys.
//
// AGGREGATION_STRATEGY: for the LAYER being requested (not the carrier), how
// the walker combines host + carrier entries when both exist.
//   - 'host_wins' (default): host's non-empty value overrides carrier.
//   - 'prepend':  carriers prepend before host, dedupe by UUID.
//   - 'append':   carriers append after host, dedupe by UUID.
//
// P1 shipped this empty/default; P2 (2026-05-28) added the 5 classifications;
// P4 Stage B (2026-05-29) adds the 3 taxonomy carriers AT THE TOP — list IS
// the priority order (template > sub_type > type). Walker iterates in array
// order and the first carrier returning a value wins.
export const DEFAULT_PROVIDER_LAYERS: readonly string[] = [
    // P4 Stage B (2026-05-29) taxonomy — descent priority 30/20/10.
    'creature_template',
    'creature_sub_type',
    'creature_type',
    // P2 (2026-05-28) classifications — priority 0, order among themselves doesn't matter.
    'weapon_classification',
    'weapon_ammo_classification',
    'armor_classification',
    'instrument_classification',
    'armor_type',
];

export const AGGREGATION_STRATEGY: Record<string, 'host_wins' | 'prepend' | 'append'> = {
    // P4 Stage B prepend (taxonomy primary-type chain semantics):
    creature_type:     'prepend',
    creature_sub_type: 'prepend',
    creature_template: 'prepend',
    // P4 Stage B append (taxonomy stacking — racial/template additions concat after host):
    attack_ability:                            'append',
    passive_ability:                           'append',
    opportunity_attack_ability:                'append',
    ability:                                   'append',
    free_ability:                              'append',
    inventory_equipped:                        'append',
    inventory_unequipped:                      'append',
    natural_weapon:                            'append',
    natural_armor:                             'append',
    contesting_roll_adv_vs_status_effect:      'append',
    contesting_roll_dis_vs_status_effect:      'append',
    // skill_tier_* handled via prefix check in _ftAggregationStrategy.
};

// Creature defense layer key → computed.creature.defenses sub-key. KEEP IN SYNC with PHP
// FT_CREATURE_DEFENSE_SLOTS (build_item_arr.php) and the JS mirror (item-arr-readers.js).
// These keys are also the "always-append" defense set (computed store ∪ carrier walk).
export const FT_CREATURE_DEFENSE_SLOTS: Record<string, string> = {
    damage_type_resistance:    'resistance',
    damage_type_immunity:      'immune',
    damage_type_vulnerability: 'vulnerable',
    immune_status_effect:      'immune_status',
    // contesting_roll_*_vs_status_effect → flat UUID lists (sourced structs); bridged 2026-06-21 so
    // finalize stores them at computed.creature.defenses.<key> instead of the base layer (like immune_status).
    // (contesting_roll_*_vs_spells are scalar flags, NOT bridged here — sourced at read-time elsewhere.)
    contesting_roll_adv_vs_status_effect: 'contesting_roll_adv_vs_status_effect',
    contesting_roll_dis_vs_status_effect: 'contesting_roll_dis_vs_status_effect',
};

// Sourced boolean defenses. These remain host-wins because their computed rows
// already include every carrier/source; unlike UUID defense lists they do not
// need a read-time carrier union.
export const FT_CREATURE_PRESENCE_DEFENSE_SLOTS: Record<string, string> = {
    force_move_resistance: 'force_move_resistance',
    immune_to_forced_movement: 'immune_to_forced_movement',
};

function _ftAggregationStrategy(layerName: string): 'host_wins' | 'prepend' | 'append' {
    // Creature defense layers always accumulate (computed store ∪ carrier walk) — reader-side
    // override mirroring PHP layer_entries; the defense layers are the FT_CREATURE_DEFENSE_SLOTS keys.
    if (FT_CREATURE_DEFENSE_SLOTS[layerName] !== undefined) {
        return 'append';
    }
    if (AGGREGATION_STRATEGY[layerName] !== undefined) {
        return AGGREGATION_STRATEGY[layerName];
    }
    if (layerName.startsWith('skill_tier_')) {
        return 'append';
    }
    return 'host_wins';
}

/**
 * Generator yielding default-provider carriers from arr.item_layer_types.
 * Mirrors PHP `_ft_default_provider_carriers`. Skips bare-UUID shells and
 * non-array slots; multi-valued carrier slots iterate every entry.
 */
/**
 * Public array form of the default-provider carrier walk — used by the object card's
 * PL fallback (a vanilla weapon borrows its classification carrier's computed.pl).
 */
export function defaultProviderCarriers(arr: any): any[] {
    return Array.from(_ftDefaultProviderCarriers(arr));
}

// Player-character provider slot names: ancestry_1/2 → lineage → background → class_1..20 →
// subclass_1..20. Mirror of PHP ft_pc_provider_slot_names() and the JS twin _ftPcProviderSlotNames()
// (item-arr-readers.js). Order mirrors the legacy merge precedence.
let _ftPcProviderSlotNamesCache: string[] | null = null;
function _ftPcProviderSlotNames(): string[] {
    if (_ftPcProviderSlotNamesCache) return _ftPcProviderSlotNamesCache;
    const names = ['ancestry_1', 'ancestry_2', 'lineage', 'background'];
    for (let i = 1; i <= 20; i++) names.push('class_' + i);
    for (let i = 1; i <= 20; i++) names.push('subclass_' + i);
    _ftPcProviderSlotNamesCache = names;
    return names;
}

// Resolve a CATALOGIZED carrier ref (bare-UUID string in a default-provider slot) back to its full
// definition. Lookup order mirrors the vanilla reader: owner riding catalog (AJAX inventory mount),
// page-local creator catalog (saved-character island), then the global cache finder. Found nodes are
// memoized for the session — catalog
// defs are STATIC by invariant (same UUID → same def); misses are NOT memoized (the cache may
// simply not be filled yet at page boot). The `active` flag is a re-entrancy guard:
// findFullNestedItem's own descent calls defaultProviderCarriers, and resolving in THAT walk
// would recurse — inner calls just skip string slots, exactly the pre-Batch-3 behavior.
const _carrierRefMemo = new Map<string, any>();
let _carrierResolveActive = false;
function _isFullCatalogDef(node: any): boolean {
    return (node.items !== null && typeof node.items === 'object')
        || (node.item_layer_types !== null && typeof node.item_layer_types === 'object');
}
function _resolveCarrierRef(uuid: string, owner?: any): any | null {
    const key = uuid.toUpperCase();
    // A memo hit starts no walk, so it answers even inside a guarded walk (a carrier resolved earlier from a catalog
    // that is no longer reachable from the roots stays findable — Codex review 2026-09-27).
    const hit = _carrierRefMemo.get(key);
    if (hit !== undefined) return hit;
    if (_carrierResolveActive) return null;
    const w: any = compileAmbient();
    if (!w) return null;
    const ridingCatalog = owner?.catalog;
    const creatorCatalog = w.ftCreatorCharacter?.catalog;
    if (ridingCatalog && typeof ridingCatalog === 'object') {
        const ridingNode = ridingCatalog[uuid] ?? ridingCatalog[key] ?? ridingCatalog[uuid.toLowerCase()];
        // The creator stamps its island catalog onto each saved inventory row as the riding catalog
        // (character-creator/index.ts), so the identity-only guard below applies here too — but only to that catalog.
        if (ridingNode && typeof ridingNode === 'object' && (ridingCatalog !== creatorCatalog || _isFullCatalogDef(ridingNode))) {
            _carrierRefMemo.set(key, ridingNode);
            return ridingNode;
        }
    }
    if (creatorCatalog && typeof creatorCatalog === 'object') {
        const creatorNode = creatorCatalog[uuid] ?? creatorCatalog[key] ?? creatorCatalog[uuid.toLowerCase()];
        // The creator island also ships IDENTITY-ONLY {item_name,item_type} entries for non-carrier refs (names
        // for the side-info card, 2026-10-10). Those are never a carrier def — fall through to the walk.
        if (creatorNode && typeof creatorNode === 'object' && _isFullCatalogDef(creatorNode)) {
            _carrierRefMemo.set(key, creatorNode);
            return creatorNode;
        }
    }
    if (typeof w.ftFindFullNestedItem !== 'function') return null;
    // `armor_type` is the one DEFAULT_PROVIDER_LAYERS slot whose values are a layerless TAXONOMY (Light /
    // Medium / Heavy / Natural: identity-only catalog rows, nothing to provide). The global walk can only miss
    // for them, and it missed on every action-bar repaint (~60 ms per move, 2026-09-10). A catalog identity
    // of that type is a permanent null — the ONLY taxonomy this shortcut may name; every other provider slot
    // (creature_*, *_classification) is a full carrier def that can nest weapons and must keep the walk.
    const identity = typeof w._ftCatalogIdentity === 'function' ? w._ftCatalogIdentity(owner ?? null, uuid) : null;
    if (identity && String(identity.item_type ?? '') === 'armor_type') {
        _carrierRefMemo.set(key, null);
        return null;
    }
    _carrierResolveActive = true;
    let node: any = null;
    try { node = w.ftFindFullNestedItem(key) ?? null; } catch { node = null; }
    _carrierResolveActive = false;
    if (node !== null) _carrierRefMemo.set(key, node);
    return node;
}

/**
 * Run `fn` with bare-UUID carrier refs left UNRESOLVED (string slots skipped). For the cached-item finder's own DFS
 * (object-card/find-cached-item.ts): every def a ref could resolve to lives in a root the DFS already walks (root
 * catalogs, riding catalogs, the creator island), so resolving inside the walk only started a NESTED full walk per
 * unresolvable ref — the guard above covered walks entered through THIS resolver, not a walk entered from the
 * item-arr-readers twin or a direct finder call (PRES lane census 2026-09-27: every legacy move re-walked for a
 * layerless armor type INSIDE the walk for a private creature item).
 */
export function withCarrierRefsUnresolved<T>(fn: () => T): T {
    const prior = _carrierResolveActive;
    _carrierResolveActive = true;
    try { return fn(); } finally { _carrierResolveActive = prior; }
}

function* _ftDefaultProviderCarriers(arr: any): Generator<any> {
    if (arr === null || arr === undefined) return;
    // item_layer_types carriers (classifications + creature taxonomy). GUARD SCOPED TO THIS LOOP: a
    // player_character caster_arr has NO root item_layer_types bucket (source-table shape), so an early
    // `return` here would skip the player_characters provider walk below — making layer/picture reads
    // blind to ancestry_*/class_* providers. Mirror of PHP _ft_default_provider_carriers + the JS twin.
    if (arr.item_layer_types !== null && typeof arr.item_layer_types === 'object') {
        for (const carrierName of DEFAULT_PROVIDER_LAYERS) {
            const slot = arr.item_layer_types[carrierName];
            // Catalogized bare-UUID carrier ref (Batch 3, 2026-06-11) — resolve via the root catalog.
            if (typeof slot === 'string' && _FT_UUID_RE.test(slot)) {
                const resolved = _resolveCarrierRef(slot, arr);
                if (resolved !== null) yield resolved;
                continue;
            }
            if (slot === undefined || slot === null || typeof slot !== 'object') continue;
            // Any ARRAY slot is a multi-valued list — entries may be full nests ({items:...})
            // OR catalogized bare-UUID strings (creature_sub_type lists since Batch 3). The old
            // `typeof slot[0] === 'object'` test wrapped an all-string list as ONE bogus carrier,
            // so multi-sub_type grants (a dragon's Bite/Claw/Tail natural weapons) silently
            // vanished from every aggregating read. Mirrors PHP array_key_exists(0, $slot).
            const entries = Array.isArray(slot) ? slot : [slot];
            for (const entry of entries) {
                if (entry !== null && typeof entry === 'object') {
                    // A deduped full-nest carrier collapses to a SHELL ({items, pictures} but NO
                    // item_layer_types) because its real layers live in the root catalog
                    // (ft_dedupe_full_nests_to_shells). Treat it like a bare-UUID ref: re-resolve to
                    // the full def so classification default-providers (damage_type / equipment_slot /
                    // roll_type / weapon_type off a weapon/armor classification) keep resolving on
                    // client blobs — e.g. a creature's natural weapon whose weapon_classification
                    // serializes as a shell. Falls back to the shell when the full def isn't cached.
                    const ilt = (entry as any).item_layer_types;
                    const isShell = ilt === null || ilt === undefined || typeof ilt !== 'object'
                        || (Array.isArray(ilt) ? ilt.length === 0 : Object.keys(ilt).length === 0);
                    if (isShell) {
                        const sid = String((entry as any).items?.item_id ?? (entry as any).item_id ?? '');
                        if (sid !== '' && _FT_UUID_RE.test(sid)) {
                            const full = _resolveCarrierRef(sid, arr);
                            if (full !== null) { yield full; continue; }
                        }
                    }
                    yield entry;
                } else if (typeof entry === 'string' && _FT_UUID_RE.test(entry)) {
                    const resolved = _resolveCarrierRef(entry, arr);
                    if (resolved !== null) yield resolved;
                }
            }
        }
    }

    // player_character providers nested in place under arr.player_characters.{ancestry_1/2, lineage,
    // background, class_1..20, subclass_1..20} — each a full nest with its own item_layer_types. Yield
    // them so layerOf/layerEntriesOf/picturesOf descend into the PC's providers like a creature's
    // taxonomy carriers. No-op for non-PC arrs (no player_characters bucket). Mirror of PHP + JS twin.
    const pc = arr.player_characters;
    if (pc !== null && typeof pc === 'object') {
        for (const slotName of _ftPcProviderSlotNames()) {
            const pslot = pc[slotName];
            // ROUND-5 #5: catalogized pc provider slots (bare-UUID refs) resolve to their full
            // def for the carrier descent — mirror of the item_layer_types leg (Batch 3).
            if (typeof pslot === 'string' && _FT_UUID_RE.test(pslot)) {
                const resolvedSlot = _resolveCarrierRef(pslot, arr);
                if (resolvedSlot !== null) yield resolvedSlot;
                continue;
            }
            if (pslot === null || pslot === undefined || typeof pslot !== 'object') continue;
            const pentries = (Array.isArray(pslot) && pslot.length > 0 && typeof pslot[0] === 'object'
                && (pslot as any).items === undefined
                && (pslot as any).item_layer_types === undefined)
                ? pslot
                : [pslot];
            for (const pe of pentries) {
                if (pe !== null && typeof pe === 'object' && (pe.items !== undefined || pe.item_layer_types !== undefined)) {
                    yield pe;
                }
            }
        }
    }
}

/**
 * UUID extractor for layer list entries — mirror of PHP `layer_list_entry_uuid`.
 * Handles bare strings, rich entries, bucketed shells, and full-nested arrs.
 */
function _ftLayerListEntryUuid(entry: any): string {
    if (typeof entry === 'string') {
        return entry.indexOf('|') !== -1 ? entry.split('|', 2)[0]! : entry;
    }
    if (entry === null || typeof entry !== 'object') return '';
    const bucketedId = entry.items?.item_id;
    if (typeof bucketedId === 'string' && bucketedId !== '') {
        return bucketedId.indexOf('|') !== -1 ? bucketedId.split('|', 2)[0]! : bucketedId;
    }
    if (typeof entry.item_id === 'string' && entry.item_id !== '') {
        return entry.item_id.indexOf('|') !== -1 ? entry.item_id.split('|', 2)[0] : entry.item_id;
    }
    if (typeof entry.item_layer_effect === 'string' && entry.item_layer_effect !== '') {
        return entry.item_layer_effect.indexOf('|') !== -1 ? entry.item_layer_effect.split('|', 2)[0] : entry.item_layer_effect;
    }
    return '';
}


/**
 * Shape-tolerant read of a single-valued layer slot, sister to PHP's
 * `layer_value()`. Phase 3 Target B (2026-05-27) flipped query_item_arr to
 * emit scalar UUIDs for single-valued layers (`obj.size = 'uuid'`) instead of
 * the legacy wrapped form (`obj.size = ['uuid']`). Existing TS callsites that
 * read `obj.size?.[0]` return the first CHAR of the scalar string — the
 * descalar trap. Pre-B blobs from Redis still arrive wrapped during cache
 * rollover; this helper handles both.
 *
 * Multi-valued layers (damage_type, requires_feat, …) are still wrapped
 * arrays. Calling layerValue() on them returns the first entry — same as
 * `arr[0]` did before.
 *
 * Usage:
 *   const size_id = layerValue<string>(obj.size, 'DC23AA88-…');
 *   const aoe_id  = layerValue<string>(itemArr.aoe_type, AoeTypeId.cube);
 */
export function layerValue<T>(slot: T | T[] | null | undefined, fallback?: T): T | undefined {
    if (slot === null || slot === undefined) return fallback;
    let value: any = slot;
    if (Array.isArray(value)) {
        value = value[0];
    }
    // Shell / full-nest unwrap. Bucketed shell shape (2026-05-28 of
    // [[project_item_arr_bucket_reshape]]): { items: {item_id, …}, item_layer_types?: {…} }.
    // Legacy 3-field shell: { item_id, item_name, item_type }. Full-nested
    // item_arr blob also carries item_id via the bucketed `items` bucket.
    // Return item_id string so `layerValue(arr.size) === '<uuid>'` callsites
    // keep working; consumers wanting the full shell read `arr.size` raw and
    // walk `['items'][…] / ['item_layer_types'][…]`.
    if (
        value !== null && typeof value === 'object'
        && (value as any).items
        && typeof (value as any).items.item_id === 'string'
    ) {
        return (value as any).items.item_id as T;
    }
    if (value !== null && typeof value === 'object' && typeof (value as any).item_id === 'string') {
        return (value as any).item_id as T;
    }
    return (value as T | undefined) ?? fallback;
}


/**
 * Boolean layer flag (checkbox-system layers like persistent_aoe / aoe_follows_target),
 * tolerant of every storage shape: legacy wrapped ['1'], bare string '1', and the
 * de-stringed integer 1 (checkbox de-string, FT_OBJECT_SHAPE_V 16, 2026-06-11).
 * Absent / null / '0' / 0 → false. Replaces the `slot?.[0] == '1'` idiom, which only
 * worked on bare strings by string-index luck ('1'[0] === '1') and breaks on a number.
 */
export function layerFlagOn(slot: unknown): boolean {
    const v: any = Array.isArray(slot) ? (slot as any[])[0] : slot;
    if (v === null || v === undefined) return false;
    return Number(v) === 1;
}


/**
 * Bare PL number off computed.pl — TS twin of PHP pl_total(). computed.pl is de-scalared
 * to a bare number everywhere (2026-06-11, including the caster root); this tolerates that
 * AND the legacy {0:n, total_gp, breakdown} object shape on warm cached blobs. Returns
 * undefined when absent/non-numeric. (computedValue() needs an array bucket, so it returns
 * undefined on the bare-number shape — don't use it for pl.)
 */
export function plTotal(arr: any): number | undefined {
    let pl = arr?.computed?.pl ?? arr?.pl;
    if ((pl === null || pl === undefined) && _ftIsInventoryInstance(arr)) {
        pl = _ftInventoryTemplateDef(arr)?.computed?.pl;
    }
    if (pl === null || pl === undefined) return undefined;
    const v: any = (typeof pl === 'object') ? (pl[0] ?? (pl as any)['0']) : pl;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
}


/**
 * plPretty — the DISPLAY form of a Power Level. TS twin of PHP ft_pl_pretty(); keep the two
 * in step. Owner call 2026-08-15: a raw badge reading "2197.41" is a wall of digits, and two
 * decimals stop meaning anything past ~100 — so the badge abbreviates YouTube-view-count style
 * and the exact figure lives one hover/click away (tooltip + openPowerLevelInfo()).
 *
 * ONE rule: round to 3 significant figures, trim trailing zeros, abbreviate >= 1000 with
 * k / M / B. Precision scales with magnitude — it survives on a 0.05 PL cantrip and evaporates
 * on a 2.2k PL wendigo:
 *
 *   0.05 -> 0.05    4.5   -> 4.5    99.5   -> 99.5   999.6   -> 1k      12450   -> 12.5k
 *   0.4  -> 0.4     12.67 -> 12.7   143.67 -> 144    2197.41 -> 2.2k    1250000 -> 1.25M
 *
 * Rounding is half-up and happens BEFORE the unit is chosen, so 999.6 promotes to "1k" instead
 * of rendering the nonsense "1000". Returns '' for null/undefined/non-numeric input.
 *
 * Two deliberate exceptions to the sig-fig rule, both out past anything a PL can reach: decimals
 * are clamped at 3 below 1 (0.01267 -> "0.013"), and B is the last unit, so >= 1e12 keeps a wider
 * mantissa ("1,234B"). The live catalog tops out around 15k.
 */
export function plPretty(pl: unknown): string {
    if (pl === null || pl === undefined || pl === '') return '';
    // Match PHP is_numeric()'s grammar before coercing. Number() is far laxer — Number(' ') is 0,
    // Number('0x1f') is 31, Number([]) is 0 — so a junk value would render a confident "0" badge on
    // the client and an empty one on the server. Only decimal/scientific strings and real numbers pass.
    if (typeof pl === 'string' && !/^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(pl)) return '';
    if (typeof pl !== 'number' && typeof pl !== 'string') return '';
    const n = Number(pl);
    if (!Number.isFinite(n)) return '';

    const sign = n < 0 ? '-' : '';
    const abs = Math.abs(n);
    if (abs === 0) return '0';

    // Trim trailing zeros off a fixed-decimal render: 4.50 -> "4.5", 144.0 -> "144".
    const trim = (v: number, decimals: number): string => {
        const s = v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
    };
    // Half-up to N decimals. toPrecision(15) sheds the binary-float dust that would otherwise make
    // 1.005 * 100 = 100.49999999999999 round DOWN; ft_pl_pretty() normalizes identically via
    // sprintf('%.15g') so the two stay bit-identical (360k-value parity sweep, 2026-08-15).
    const round = (v: number, decimals: number): number => {
        const f = Math.pow(10, decimals);
        return Math.round(Number((v * f).toPrecision(15))) / f;
    };

    if (abs >= 1000) {
        for (const [suffix, div] of [['B', 1e9], ['M', 1e6], ['k', 1e3]] as Array<[string, number]>) {
            if (abs < div) continue;
            // 3 sig figs on the mantissa (which lives in [1, 1000)).
            const raw = abs / div;
            const decimals = raw < 10 ? 2 : (raw < 100 ? 1 : 0);
            const v = round(raw, decimals);
            // Rounding can push the mantissa to 1000 (999.6 -> 1000k); promote a unit instead.
            if (v >= 1000 && suffix !== 'B') return `${sign}1${suffix === 'k' ? 'M' : 'B'}`;
            return `${sign}${trim(v, decimals)}${suffix}`;
        }
    }

    // Below 1000: 3 sig figs, decimals clamped to 3 so a hair-thin PL can't spray digits. The clamp
    // is a deliberate exception to the sig-fig rule (0.01267 -> "0.013", not "0.0127") — a badge is
    // not a lab readout, and calc_pl_system already rounds every PL to 2 decimals anyway.
    const decimals = Math.max(0, Math.min(3, 2 - Math.floor(Math.log10(abs))));
    const rounded = round(abs, decimals);
    // Nonzero but too thin to render: keep the inequality pointing the way the number actually lies.
    if (rounded === 0) return sign === '-' ? '>-0.001' : '<0.001';
    if (rounded >= 1000) return `${sign}1k`; // 999.6 -> 1000 -> promote
    return `${sign}${trim(rounded, decimals)}`;
}


/**
 * plExact — the UNABBREVIATED PL for the badge tooltip, so plPretty()'s abbreviation never hides a
 * number the reader wanted. Comma thousands + 2 decimals only on a real fractional part; PHP twin:
 * make_number_pretty(), which is what the server-rendered tooltips use — same output, both sides.
 *
 * "Exact" in the sense that matters here: calc_pl_system stores every PL as round($gp / 100, 2), so
 * 2 decimals IS the stored precision. It is not a raw-float dump and shouldn't become one.
 */
export function plExact(pl: unknown): string {
    // Same is_numeric() grammar gate as plPretty() — the two are rendered as a PAIR (badge + its
    // tooltip), so a value one rejects must not sneak through the other as a confident "0".
    if (pl === null || pl === undefined || pl === '') return '';
    if (typeof pl === 'string' && !/^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(pl)) return '';
    if (typeof pl !== 'number' && typeof pl !== 'string') return '';
    const n = Number(pl);
    if (!Number.isFinite(n)) return '';
    const hasFraction = Math.abs(n - Math.round(n)) > 0.000001;
    return hasFraction
        ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : Math.round(n).toLocaleString('en-US');
}


/**
 * Shape-tolerant read of a map coordinate, sister to PHP `pos_coord()`.
 * 2026-06-02: the container was renamed `pos_arr` -> `position` and its
 * coordinates descalared from the legacy `[0]`-wrapped form (`{"x_pos":["-5.6"]}`)
 * to bare scalars (`{"x_pos":-5.6}`). On a bare number, the old
 * `obj.pos_arr?.x_pos?.[0]` read returns `undefined` (then `?? 0` collapses the
 * token to the origin) — this helper kills that trap.
 *
 * Reads `position` (preferred) or legacy `pos_arr`, accepts a parent arr OR a
 * bare container passed directly, and unwraps a `[0]` if the cached blob still
 * carries the old shape during the deploy rollover. Returns a number, or
 * `undefined` when the coordinate is absent / non-numeric.
 *
 * Usage:
 *   const x = posCoord(creature, 'x_pos') ?? 0;
 */
export function posCoord(arr: any, coord: 'x_pos' | 'y_pos' | 'z_pos'): number | undefined {
    if (arr === null || arr === undefined) return undefined;
    let container: any = null;
    if (arr.position != null && typeof arr.position === 'object') {
        container = arr.position;
    } else if (arr.pos_arr != null && typeof arr.pos_arr === 'object') {
        container = arr.pos_arr;
    } else if (arr[coord] !== undefined) {
        // A bare container passed directly (already pulled off the parent).
        container = arr;
    }
    if (container == null) return undefined;
    let v: any = container[coord];
    if (Array.isArray(v)) v = v[0];
    if (v === null || v === undefined || v === '') return undefined;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isNaN(n) ? undefined : n;
}


// ─────────────────────────────────────────────────────────────────────────────
// Bucket-by-source-table reader helpers — [[project_item_arr_bucket_reshape]].
// Producer now emits items-cols in $arr.items, layers in $arr.item_layer_types,
// computed runtime values (v_arr, pl) in $arr.computed, pictures in
// $arr.item_pictures (plural). These helpers read the bucketed path; the
// optional fallback to legacy top-level is for browser-cached blobs that
// pre-date the producer flip.
// ─────────────────────────────────────────────────────────────────────────────


/**
 * Read a status row's `status_id` tolerant of BOTH shapes: the PhpScalar `[id]` array and a bare-string
 * `id` (some rows store it un-wrapped). The pervasive `status.status_id?.[0]` assumes the array form — on a
 * bare string it returns the first CHARACTER (a UUID "248563C4-…" → "2"), which silently mis-keys the
 * persistent-AOE element id / index / double-click lookup. Returns '' when absent.
 */
export function statusIdOf(status: any): string {
    const v = status?.status_id;
    return String((Array.isArray(v) ? v[0] : v) ?? '');
}

export function itemsCol<T = string>(arr: any, key: string): T | undefined {
    if (arr === null || arr === undefined) return undefined;
    const bucketed = arr.items != null ? arr.items[key] : undefined;
    if (bucketed !== undefined && bucketed !== null) {
        return (Array.isArray(bucketed) ? bucketed[0] : bucketed) as T;
    }
    const flat = arr[key];
    if (flat !== undefined && flat !== null) {
        return (Array.isArray(flat) ? flat[0] : flat) as T;
    }
    // Status-entry identity bridge (ROUND-7 #8 — twin of PHP item_field + JS ftItemsCol, which
    // had it first): lean status rows carry identity in the status def. ROUND-9 #11: the
    // collapsed ref rides under `status_item_id` (honest DB column name); a bare string under
    // status_arr is the legacy v27/v28 warm form — item_id IS the ref; name/type via catalogs.
    if (
        (key === 'item_type' || key === 'item_name' || key === 'item_id')
        && (arr.status_arr != null || arr.status_item_id != null)
    ) {
        const sa = arr.status_arr;
        if (sa != null && typeof sa === 'object') {
            const v = itemsCol(sa, key);
            if (v !== undefined && v !== null) return v as T;
        } else {
            const ref = (typeof arr.status_item_id === 'string' && arr.status_item_id !== '')
                ? arr.status_item_id
                : ((typeof sa === 'string' && sa !== '') ? sa : '');
            if (ref !== '') {
                if (key === 'item_id') return ref as unknown as T;
                const ident = _ftCatalogIdentity(arr, ref);
                const v = ident?.[key];
                if (v !== undefined && v !== null) return (Array.isArray(v) ? v[0] : v) as T;
            }
        }
    }
    // Thinned template-pointer fallback (gear-template catalogize v23; ROUND-6 #1 widened):
    // inventory instances carry NO items bucket at all — the template id rides
    // inventory.inventory_item_id, and identity/created_by live ONCE in the root `.catalog`.
    // Resolve via the node's riding catalog or the ambient cache roots' catalogs. Mirrors PHP
    // item_field()'s gear bridge. item_id answers straight from the carrier.
    const tplId = _ftInventoryTemplateId(arr);
    if (tplId !== '') {
        if (key === 'item_id' && _ftIsInventoryInstance(arr)) return tplId as unknown as T;
        if (key === 'item_name' || key === 'item_type' || key === 'flavor_text'
            || key === 'item_created_by_id' || key === 'item_description' || key === 'homebrew') {
            const ident = _ftCatalogIdentity(arr, tplId);
            const v = ident?.[key];
            if (v !== undefined && v !== null) return (Array.isArray(v) ? v[0] : v) as T;
        }
    }
    return undefined;
}

/**
 * ROUND-6 #1: the TEMPLATE item_id of an inventory instance — items.item_id on warm (pre-v26)
 * blobs, inventory.inventory_item_id once the pointer bucket is dropped. '' when neither
 * resolves. KEEP IN SYNC with PHP _ft_inventory_template_id + the JS twin.
 */
function _ftInventoryTemplateId(arr: any): string {
    if (!arr || typeof arr !== 'object') return '';
    let tid = arr.items?.item_id ?? arr.inventory?.inventory_item_id ?? arr.inventory_item_id;
    if (Array.isArray(tid)) tid = tid[0];
    return (typeof tid === 'string') ? tid : '';
}

// Catalog identity lookup for thinned template pointers: the node's own riding catalog first
// (inv_p tree entries carry one), then the ambient cache roots' catalogs (casterCache /
// objectCache / encounterCache — the host union). Hit-memoized per id (catalog identities are
// static within a session). Normalizes identity entries ({item_name,item_type,flavor_text?}) and
// hoisted full defs ({items:{...}}) to one shape.
type CatalogIdentity = Record<string, unknown>;

const _ftTemplateIdentMemo: Record<string, CatalogIdentity> = {};
function _ftCatalogIdentity(node: any, id: string): CatalogIdentity | undefined {
    const norm = (e: unknown): CatalogIdentity | undefined => {
        if (!e || typeof e !== 'object') return undefined;
        const record = e as Record<string, unknown>;
        return (record.items && typeof record.items === 'object')
            ? record.items as CatalogIdentity
            : record;
    };
    const ride = node?.catalog;
    if (ride && typeof ride === 'object') {
        const n = norm(ride[id] ?? ride[id.toUpperCase()] ?? ride[id.toLowerCase()]);
        if (n) return n;
    }
    const memoK = id.toUpperCase();
    if (Object.prototype.hasOwnProperty.call(_ftTemplateIdentMemo, memoK)) return _ftTemplateIdentMemo[memoK];
    const ft = (compileAmbient() as any).FateTide;
    for (const m of [ft?.casterCache, ft?.objectCache, ft?.encounterCache]) {
        if (!m || typeof m.values !== 'function') continue;
        for (const root of m.values()) {
            const cat = root?.catalog;
            if (!cat || typeof cat !== 'object') continue;
            const n = norm(cat[id] ?? cat[id.toUpperCase()] ?? cat[id.toLowerCase()]);
            if (n) { _ftTemplateIdentMemo[memoK] = n; return n; }
        }
    }
    return undefined;
}

/**
 * Public catalog-identity lookup: resolve a template id to its interned identity
 * ({item_name, item_type, ...}) from the node's own riding catalog, then the ambient cache
 * roots' catalogs. Normalizes both the identity-shell ({item_name,...}) and hoisted full-def
 * ({items:{...}}) catalog shapes. Use this when a caller needs the TRUE template name for an id
 * and CANNOT rely on itemsCol(item_name) — e.g. a standalone inventory item whose items.item_name
 * was overwritten with the instance display name by ft_object_api_hydrate. undefined on a miss.
 */
export function catalogIdentity(node: any, id: string | null | undefined): any | undefined {
    if (!id) return undefined;
    return _ftCatalogIdentity(node, id);
}

// ─────────────────────────────────────────────────────────────────────────────
// ROUND-4 "inventory instance = pure template ref" (2026-06-12). Thinned inventory
// instances carry NO item_layer_types (byte-compare-dropped into the root-catalog
// template def) and may lack pictures.item + the presentation/thumbnail stamp rows. These
// helpers resolve the FULL def for the layer/picture bridges below. Server twin:
// _ft_resolve_inventory_instance_full (resolves the instance's own fat compile);
// the client resolves the catalog def — byte-equal to the dropped content by
// construction (an instance only thins when identical to the def in its blob).
// ─────────────────────────────────────────────────────────────────────────────

/** inventory-instance check: an inventory bucket (or legacy flat key) with a non-empty id. */
function _ftIsInventoryInstance(arr: any): boolean {
    if (!arr || typeof arr !== 'object') return false;
    let iid = arr.inventory?.inventory_id ?? arr.inventory_id;
    if (Array.isArray(iid)) iid = iid[0];
    return typeof iid === 'string' && iid !== '';
}

// Volatile per-instance layers the thin keeps as a RESIDUAL ilt on the instance (defs are
// template-pure). KEEP IN SYNC with PHP FT_INVENTORY_VOLATILE_INSTANCE_LAYERS + the JS twin.
const _FT_INV_VOLATILE_LAYERS: Record<string, true> = { current_item_charges: true };

/** ROUND-11 #3 (v32): ENCHANTED inventory instance — inventory.enchantment_template_id (or the
 *  legacy flat key) non-empty, or a computed.enchantment bucket. KEEP IN SYNC with PHP
 *  _ft_inventory_instance_is_enchanted + the JS twin. */
function _ftIsEnchantedInventoryInstance(arr: any): boolean {
    if (!arr || typeof arr !== 'object') return false;
    let et = arr.inventory?.enchantment_template_id;
    if (et === undefined || et === null || et === '') et = arr.enchantment_template_id;
    if (Array.isArray(et)) et = et[0];
    if (typeof et === 'string' && et !== '') return true;
    return !!(arr.computed && typeof arr.computed === 'object' && (arr.computed as any).enchantment);
}

/** True for an inventory-instance node whose item_layer_types was thinned away — absent, or
 *  reduced to the volatile residual. Kept-full UNENCHANTED instances (divergent) must NOT bridge.
 *  ROUND-11 #3 (v32): ENCHANTED instances thin PER-LAYER (the enchantment delta + divergents
 *  stay), so the residual-shape test can't identify them — bridge on the enchanted marker. A
 *  missed key resolves from the catalog def, which is safe: the enchant layering only ever ADDS
 *  or MUTATES layers (kept in-tree, read first), never removes one. */
function _ftIsThinnedInventoryInstance(arr: any): boolean {
    if (!_ftIsInventoryInstance(arr)) return false;
    // ROUND-6 #1: the items pointer bucket is dropped — gate via the template-id helper
    // (items.item_id on warm blobs ?? inventory.inventory_item_id).
    if (_ftInventoryTemplateId(arr) === '') return false;
    const ilt = arr.item_layer_types;
    if (ilt == null || typeof ilt !== 'object') return true;
    if (Object.keys(ilt).every((k) => _FT_INV_VOLATILE_LAYERS[k])) return true;
    return _ftIsEnchantedInventoryInstance(arr);
}

/**
 * Full template-def lookup for an inventory instance: riding catalog (inv_p tree shape) →
 * ambient cache roots' catalogs → the session finder (_resolveCarrierRef). Requires
 * item_layer_types on the entry (an identity row can't serve layer reads). Hit-memoized —
 * defs are static by the catalog invariant (a per-instance change recompiles + re-compares,
 * so a dropped copy is always byte-equal to the def it resolves to).
 */
const _ftInvDefMemo = new Map<string, any>();
function _ftInventoryTemplateDef(node: any): any | null {
    const id = _ftInventoryTemplateId(node); // ROUND-6 #1: inventory.inventory_item_id carrier
    if (id === '') return null;
    const isFull = (e: any): boolean =>
        !!(e && typeof e === 'object' && e.items && typeof e.items === 'object'
            && (e.item_layer_types !== undefined || e.assets !== undefined || e.computed !== undefined));
    const ride = node?.catalog;
    if (ride && typeof ride === 'object') {
        const e = ride[id] ?? ride[id.toUpperCase()] ?? ride[id.toLowerCase()];
        if (isFull(e)) return e;
    }
    const memoK = id.toUpperCase();
    const hit = _ftInvDefMemo.get(memoK);
    if (hit !== undefined) return hit;
    const ft = (compileAmbient() as any).FateTide;
    for (const m of [ft?.casterCache, ft?.objectCache, ft?.encounterCache]) {
        if (!m || typeof m.values !== 'function') continue;
        for (const root of m.values()) {
            const cat = root?.catalog;
            if (!cat || typeof cat !== 'object') continue;
            const e = cat[id] ?? cat[id.toUpperCase()] ?? cat[id.toLowerCase()];
            if (isFull(e)) { _ftInvDefMemo.set(memoK, e); return e; }
        }
    }
    const found = _resolveCarrierRef(id);
    if (isFull(found)) { _ftInvDefMemo.set(memoK, found); return found; }
    return null;
}


// ─────────────────────────────────────────────────────────────────────────────
// Shell-catalog resolvers — Phase 5 stage 2 of the data-shape leanup
// (plans/handoff-leanup-final-phases.md, [[project_data_shape_leanup_master]]).
//
// Stage 1 interned every embedded identity shell into a root
// `catalog: {uuid => {item_name,item_type}}`. Stage 3 collapses DUPLICATE full
// nests — across EC members and inside computed.creature baked nests — to 3-field
// shells (keeps name/type, drops item_layer_types/computed). These readers let a
// consumer that arrives at a shell recover (a) the full in-blob copy by id, and
// (b) identity (name/type) from the root catalog — tolerating BOTH today's
// embedded shape AND the post-stage-3 collapsed shape. They are a NO-OP until
// stage 3 emits the collapsed shape (nothing is shelled across members yet), so
// shipping them now changes no rendered output.
//
// fullNodeById generalizes the ability-row.ts `fullAbilityById` precedent (an
// abilities-only blob index) to ANY item_type — spells, natural_weapon,
// weapon_classification, creature, etc. — the types stage 3 will collapse.
// ─────────────────────────────────────────────────────────────────────────────

export interface FullItemNode extends Record<string, unknown> {
    items?: Record<string, unknown> & { item_id?: unknown };
    item_layer_types?: Record<string, unknown>;
}

export type FullNodeIndex = Record<string, FullItemNode>;

const _ftFullNodeByIdCache = new WeakMap<object, FullNodeIndex>();

/**
 * Blob-wide index: item_id (lowercased) -> first FULL nested copy (a node with a
 * non-empty item_layer_types). First full copy wins — matches the keep-first
 * canonicalization the PHP dedupe uses, so the indexed node is the canonical one
 * stage 3 keeps full.
 *
 * ⚠ WeakMap-cached on `root`, which is WEAKER than it looks. The previous note here claimed
 * "a fresh blob (e.g. a WS repaint hands a new object) gets a fresh walk" — that premise is
 * FALSE and it is the exact belief that caused the 2026-07-20 action-bar fold-out bug. A WS
 * repaint does NOT hand a new object: caster-delta.ts patches sections onto the cached blob,
 * and adoptCasterBlobIntoEncounterRow / encounter-delta.ts swap a row's contents while KEEPING
 * the row object (deliberately — every alias must stay valid). So for a long-lived cache root
 * this index can be stale, and object identity can never be a freshness signal anywhere in the
 * render layer.
 *
 * Currently tolerable only because resolveNestedRef (the sole caller, :698) falls through to
 * window.ftFindFullNestedItem on a miss, and THAT finder memoizes per-task (queueMicrotask
 * clear, find-cached-item.ts:144) rather than per-object — the correct pattern here. If you add
 * a caller that trusts this index without that fallback, convert it to the task-scoped memo first.
 */
export function fullNodeById(root: any): FullNodeIndex {
    if (!root || typeof root !== 'object') return {};
    const cached = _ftFullNodeByIdCache.get(root);
    if (cached) return cached;
    const idx: FullNodeIndex = {};
    const seen = new Set<any>();
    const walk = (o: any): void => {
        if (!o || typeof o !== 'object' || seen.has(o)) return;
        seen.add(o);
        if (Array.isArray(o)) { for (const v of o) walk(v); return; }
        const id = o?.items?.item_id;
        if (
            typeof id === 'string' && id !== ''
            && o.item_layer_types && typeof o.item_layer_types === 'object'
        ) {
            const k = id.toLowerCase();
            if (!idx[k]) idx[k] = o as FullItemNode; // first FULL copy wins
        }
        for (const key of Object.keys(o)) walk(o[key]);
    };
    walk(root);
    _ftFullNodeByIdCache.set(root, idx);
    return idx;
}

/**
 * Resolve a layer entry to its FULL nest. If `entry` already carries
 * item_layer_types it is returned as-is; otherwise its UUID
 * (`_ftLayerListEntryUuid` tolerates bare strings, bucketed shells, and full
 * arrs) is looked up in `fullNodeById(root)`. Falls back to the entry itself (a
 * shell) when no full copy exists anywhere — callers needing layer data must
 * still guard for the shell. Type-agnostic generalization of
 * ability-row.ts `resolveAbilityNest`.
 */
export function resolveNest(entry: any, root?: any): any {
    if (entry && typeof entry === 'object' && (entry as any).item_layer_types) {
        return entry;
    }
    const id = _ftLayerListEntryUuid(entry);
    if (id && root) {
        const full = fullNodeById(root)[id.toLowerCase()];
        if (full) return full;
    }
    // Global fallback (parity with ability-row.ts resolveAbilityNest / abilityNest, 2026-06-29): the
    // fullNodeById(root) walk above only sees defs PHYSICALLY inside `root`, so a BARE-UUID-STRING
    // entry whose full def lives in ANOTHER cache entry resolves to nothing and the caller (castable
    // tab, list-display, class table) drops or degrades it. This is the exact bug that hid the bare-
    // string Paladin attack abilities: an encounter-re-seeded member blob loses its own `.catalog`
    // (unioned up to the encounter root), so the def isn't in `root`. Resolve via the global
    // cache/catalog finder — the SAME one _resolveCarrierRef uses (window-attached; we can't import
    // findFullNestedItem here, it imports this module). Tolerates a not-yet-attached finder by
    // returning the entry unchanged (prior behavior). Memoization lives inside the finder.
    if (id) {
        const w: any = compileAmbient();
        if (w && typeof w.ftFindFullNestedItem === 'function') {
            try {
                const full = w.ftFindFullNestedItem(id);
                if (full !== null && full !== undefined) return full;
            } catch { /* finder not ready at this paint — fall through to the entry */ }
        }
    }
    return entry;
}

/**
 * Root-catalog identity lookup: uuid -> {item_name, item_type}. The last-resort
 * name/type source for a node that arrives as a bare ref carrying neither an
 * embedded shell nor a resolvable full copy. Lowercase/uppercase-tolerant since
 * the catalog is keyed by the raw (possibly mixed-case) item_id.
 */
export function catalogShell(
    root: any,
    uuid: string | null | undefined,
): { item_name?: string; item_type?: string } | undefined {
    if (!root || typeof root !== 'object' || !uuid) return undefined;
    const cat = (root as any).catalog;
    if (!cat || typeof cat !== 'object') return undefined;
    return cat[uuid] ?? cat[uuid.toLowerCase()] ?? cat[uuid.toUpperCase()];
}


// inventoryField — TS twin of PHP inventory_field() / JS ftInventoryField. Reads `inventory`-table
// columns from the source-table bucket (descalared bare) with a legacy top-level fallback (bare or
// [0]-wrapped) for pre-reshape cache blobs. Item identity (item_id/item_type/item_name) is NOT here
// -- use itemsCol() for those. Sparse projection defaults match PHP: missing item_qty means 1 and a
// missing thumbnail pointer means the presentation pointer, but only on a recognizable inventory row.
export function inventoryField<T = string>(arr: any, key: string): T | undefined {
    if (arr === null || arr === undefined) return undefined;
    const bucket = arr.inventory;
    if (bucket != null && typeof bucket === 'object' && !Array.isArray(bucket) && bucket[key] !== undefined) {
        const v = bucket[key];
        return (Array.isArray(v) ? v[0] : v) as T;
    }
    const flat = arr[key];
    if (flat !== undefined && flat !== null) return (Array.isArray(flat) ? flat[0] : flat) as T;
    const inventoryId = bucket != null && typeof bucket === 'object' && !Array.isArray(bucket)
        ? bucket.inventory_id
        : undefined;
    const recognizableId = Array.isArray(inventoryId) ? inventoryId[0] : inventoryId;
    if (typeof recognizableId === 'string' && recognizableId !== '') {
        if (key === 'item_qty') return 1 as T;
        if (key === 'inventory_thumbnail_pic_id') {
            const presentationId = bucket.inventory_presentation_pic_id;
            if (presentationId !== undefined) {
                return (Array.isArray(presentationId) ? presentationId[0] : presentationId) as T;
            }
        }
    }
    return undefined;
}


// Bare-scalar read of a flat "pure-row" container field. Sister to the descalar
// pattern proven for items/locations/campaigns. The 2026-06-04/05 reshape dropped
// the [0] wrap on two pure-row tables -- encounters (build_encounter_arr) and
// encounter_creatures (build_encounter_creature_arr) -- so their own-row columns are
// now bare scalars at the container's top level. Legacy `arr.col?.[0]` reads return
// the first CHAR of the bare string (the descalar trap); these readers kill that and
// still unwrap a [0] from an in-memory cached blob during deploy rollover.
function _ftBareScalar<T>(container: any, key: string): T | undefined {
    if (container === null || container === undefined) return undefined;
    const v = container[key];
    if (v === undefined || v === null) return undefined;
    return (Array.isArray(v) ? v[0] : v) as T;
}

// encounterField — reads an encounter OWN-ROW column off the encounter arr. Bucket reshape
// (2026-06-09): build_encounter_arr.php now nests the encounters-table columns under the
// `encounters` source-table bucket (bare scalars), mirroring items/locations/campaigns. Read the
// bucket first, then fall back to the legacy flat top-level slot (warm cached blobs from the
// 2026-06-04/05 descalar). Both legs unwrap a stray [0] (deploy/cache rollover). Mirror of the PHP
// encounter_field() reader.
export function encounterField<T = string>(arr: any, key: string): T | undefined {
    if (arr === null || arr === undefined) return undefined;
    const bucket = arr.encounters;
    const keys = key === 'encounter_tempurature'
        ? ['encounter_tempurature', 'encounter_temperature']
        : [key];
    let v: any;
    for (const lookupKey of keys) {
        if (bucket !== null && bucket !== undefined && typeof bucket === 'object' && lookupKey in bucket) {
            v = bucket[lookupKey];
            break;
        }
        if (lookupKey in arr) {
            v = arr[lookupKey];
            break;
        }
    }
    if (v === undefined || v === null) return undefined;
    if (Array.isArray(v)) v = v[0];
    // Catalog shell unwrap (2026-06-09): env / light / weather / location_type are shelled
    // {items:{item_id,...}} inside the bucket. Return the UUID (icon lookups / seeds); read the name
    // via arr.encounters[key].items.item_name.
    if (v !== null && typeof v === 'object' && (v as any).items && typeof (v as any).items.item_id === 'string') {
        return (v as any).items.item_id as T;
    }
    return v as T;
}

// setEncounterField — write `value` into the SAME slot encounterField() reads, PRESERVING the
// catalog-shell shape for shelled keys. env / light / weather / location_type live in the
// `encounters` bucket as {items:{item_id,item_name}} display carriers (build_encounter_arr.php); a
// naive `bucket[key] = uuid` would replace that shell with a bare string and break every
// `...[key].items.item_name` read (the sidelog summary). For a shelled slot we set
// `.items.item_id` (and `.items.item_name` when a name is supplied); for a bare slot or a legacy
// root field we write the value directly. Used by the WS mutation-time scalar-authority reconcile to
// assert the authoritative light/weather UUID over a stale full push without corrupting the blob.
export function setEncounterField(arr: any, key: string, value: any, itemName?: string): void {
    if (arr === null || arr === undefined || typeof arr !== 'object') return;
    const bucket = arr.encounters;
    if (bucket !== null && bucket !== undefined && typeof bucket === 'object' && key in bucket) {
        // Nullable catalog overrides (currently encounter_light) must become a real null slot, not a
        // shell with items.item_id=null: encounterField() deliberately unwraps only string IDs.
        if (value === null || value === undefined || value === '') {
            bucket[key] = null;
            return;
        }
        const slot = bucket[key];
        if (slot !== null && typeof slot === 'object' && slot.items && typeof slot.items === 'object') {
            slot.items.item_id = value;
            if (itemName) slot.items.item_name = itemName;
            return;
        }
        bucket[key] = value;
        return;
    }
    arr[key] = value === '' ? null : value;
}

// encounterCreatureField — reads an encounter_creature OWN-ROW column off the INNER
// `encounter_creatures` object (callers pass `entity.encounter_creatures`). Mirror of
// the descalared build_encounter_creature_arr.php shape.
// Catalog shell unwrap: on the PC compile path ft_shell_keys_deep() shells team /
// preferred_speed_type / encounter_weather / encounter_environment into
// {items:{item_id,...}} display carriers (query_player_character_arr_inc.php), while
// monster ECs keep bare UUID strings — return the UUID either way so callers can keep
// string-comparing (the Delion-missing-from-combat-widget bug, 2026-06-10). Read the
// name via ec[key].items.item_name.
export function encounterCreatureField<T = string>(ec: any, key: string): T | undefined {
    const v = _ftBareScalar<T>(ec, key);
    if (v !== null && typeof v === 'object' && (v as any).items && typeof (v as any).items.item_id === 'string') {
        return (v as any).items.item_id as T;
    }
    return v;
}

// reachOf — creature/caster melee reach in feet. The reach value moved off the legacy
// top-level `reach` slot to computed.creature.reach (2026-05-30; build_item_arr.php:1116),
// stored either as `{ total: N }` or a bare scalar. Mirror of PHP layer_value($c,'reach')'s
// reach special-case. Falls back to a legacy top-level reach (bare or [0]-wrapped cached blob).
// Returns a number, or undefined when absent (callers default, e.g. `?? 5`).
export function reachOf(arr: any): number | undefined {
    if (arr === null || arr === undefined) return undefined;
    const c = arr.computed?.creature?.reach;
    let v: any = (c !== null && c !== undefined && typeof c === 'object') ? c.total : c;
    if (v === undefined || v === null) {
        // Legacy top-level slot (pre-2026-05-30 blobs / cache rollover): bare or [0]-wrapped.
        const legacy = arr.reach;
        v = Array.isArray(legacy) ? legacy[0] : legacy;
    }
    if (v === undefined || v === null || v === '') return undefined;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isNaN(n) ? undefined : n;
}

// overrunChargesRemainingOf — how many Overrun (Full Overrun free_ability) charges the creature
// has left this turn. Stamped at finalize_creature_arr.php into computed.creature.overrun_charges_remaining
// (mirrors compute.creature.reach's home) so the lean encounter-map row can gate the "no overrun charges"
// drag block (FT3D_OVERRUN) without shipping the free_ability bucket. Server normalizes NA/unlimited → 1, so
// a returned value of 0 means "blocked". Falls back to a legacy top-level slot for any pre-stamp cached blob;
// returns undefined when absent (caller decides — absence is NOT treated as blocked).
export function overrunChargesRemainingOf(arr: any): number | undefined {
    if (arr === null || arr === undefined) return undefined;
    const c = arr.computed?.creature?.overrun_charges_remaining;
    let v: any = (c !== null && c !== undefined && typeof c === 'object') ? c.total : c;
    if (v === undefined || v === null) {
        const legacy = arr.overrun_charges_remaining;
        v = Array.isArray(legacy) ? legacy[0] : legacy;
    }
    if (v === undefined || v === null || v === '') return undefined;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isNaN(n) ? undefined : n;
}

// senseValue — creature/caster special-sense range in feet (nightsight, fiendsight,
// blindsight, ghostsight, spellsight, truesight, tremorsense, telepathy). Mirror of PHP
// layer_value($c,'<sense>')'s senses bridge (build_item_arr.php): compute_senses() persists
// each sense at computed.creature.senses.<name> (stored as `{ total: N }` or a bare scalar)
// and process_int_layer_types() deliberately SKIPS senses, so they are NEVER mirrored into
// item_layer_types or the legacy top-level $item_arr[<sense>] slot. A raw slot read
// (`casterArr.truesight?.[0]`) therefore returns 0 for every sense post-2026-06-15 — the
// XZ-map "vision borked / senses never apply" bug. Falls back to the legacy top-level slot
// (bare or [0]-wrapped) for any pre-bridge cached blob. Returns 0 when absent (callers
// compare `> 0`).
export function senseValue(arr: any, senseName: string): number {
    if (arr === null || arr === undefined) return 0;
    const c = arr.computed?.creature?.senses?.[senseName];
    let v: any = (c !== null && c !== undefined && typeof c === 'object') ? c.total : c;
    if (v === undefined || v === null) {
        const legacy = arr[senseName];
        v = Array.isArray(legacy) ? legacy[0] : legacy;
    }
    if (v === undefined || v === null || v === '') return 0;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : 0;
}

// sizeIdOf — creature/caster resolved size UUID. Mirror of PHP layer_value($c,'size')'s
// size bridge (build_item_arr.php): recalc_creature_size persists the resolved UUID at
// computed.creature.size.breakdown.final_size_id, and a PC caster has NO item_layer_types
// bucket at all — so slot-style reads (`layerValue(arr.size)`) miss and either fall to a
// hardcoded default or (via the carrier walk) pick up a provider ITEM's own catalog size
// (e.g. a lineage item's Tiny — the XZ-map tiny-token bug, 2026-06-10). Falls back to the
// bucketed/legacy slots for pre-reshape cached blobs. Returns the UUID or undefined.
export function sizeIdOf(arr: any): string | undefined {
    if (arr === null || arr === undefined) return undefined;
    const bridged = arr.computed?.creature?.size?.breakdown?.final_size_id;
    if (typeof bridged === 'string' && bridged !== '') return bridged;
    const slot = (arr.item_layer_types != null && typeof arr.item_layer_types === 'object')
        ? arr.item_layer_types.size
        : arr.size;
    const v = layerValue<string>(slot as string | string[] | undefined);
    return v === undefined || v === null || v === '' ? undefined : String(v);
}


// v33 (ROUND-12 #3): VitalityStatus band uuids + % bands — static-catalog twin of
// additions.php VitalityStatus::calculateStatus (uuids copied verbatim; the catalog is
// hardcoded PHP constants, same precedent as the skills-tab group-info uuids).
const VITALITY_STATUS_IDS = {
    invigorated:    '82788F70-EBEF-44C4-80E7-6622AD20DD60',
    fresh:          'FF30B76E-785D-2CAF-FC66-C131D0548808',
    barely_wounded: '658483AA-34DE-74E5-C974-E3E202C31CC7',
    wounded:        'C97076EF-FED9-19B4-D109-C22F9BC740E8',
    bloodied:       '44B8792E-632E-2651-C666-67DFD181E92A',
    well_bloodied:  '298BE1DD-2917-476E-D79C-92CF8C598926',
    on_deaths_door: 'D5DE14C4-3194-87BC-D767-6D45BDD90552',
    dying:          '8AE129AF-52D8-CCE8-706A-85767DB52740',
} as const;

function calcVStatus(cur: number, max: number, temp: number): string {
    if (max <= 0) return VITALITY_STATUS_IDS.dying;
    const ratio = cur / max;
    if (ratio >= 1.0) return temp > 0 ? VITALITY_STATUS_IDS.invigorated : VITALITY_STATUS_IDS.fresh;
    if (ratio > 0.75) return VITALITY_STATUS_IDS.barely_wounded;
    if (ratio > 0.50) return VITALITY_STATUS_IDS.wounded;
    if (ratio > 0.25) return VITALITY_STATUS_IDS.bloodied;
    if (ratio > 0.10) return VITALITY_STATUS_IDS.well_bloodied;
    if (ratio > 0.00) return VITALITY_STATUS_IDS.on_deaths_door;
    return VITALITY_STATUS_IDS.dying;
}

/**
 * v33 (ROUND-12 #3): rehydrate a LEAN stored vitality bucket — non-living targets
 * (inventory rows / object items) store only base state; derive the full historical
 * shape (build_v_arr's non-living math: no blight). Fat blocks (living creatures /
 * pre-v33 blobs) pass through untouched — v_status is always stored on those.
 * Stored keys win (a mid-combat set_v_current write stays authoritative).
 * KEEP IN SYNC with PHP ft_hydrate_vitality (build_item_arr.php) and the JS twin
 * (item-arr-readers.js ftDeriveVitality).
 */
export function deriveVitality(v: Record<string, unknown>): Record<string, unknown> {
    if (v == null || typeof v !== 'object' || v.v_status !== undefined) return v ?? {};
    const unwrap = (x: any) => (Array.isArray(x) ? x[0] : x);
    const num = (x: any) => { const n = parseFloat(String(unwrap(x) ?? 0)); return isNaN(n) ? 0 : n; };
    const baseMax = num(v.base_v_max ?? v.v_max);
    // v38 catalog defaults store only the maximum; absent current is full health. An explicit
    // zero remains a real damage override and must not fall through this default.
    const baseCur = num(v.base_v_current !== undefined
        ? v.base_v_current
        : (v.v_current !== undefined ? v.v_current : baseMax));
    const temp    = num(v.base_v_temp ?? v.v_temp);
    const maxTemp = num(v.base_v_max_temp ?? v.v_max_temp);
    const effMax  = Math.max(1, baseMax + maxTemp);
    const cur     = Math.max(0, Math.min(baseCur, effMax));
    const total   = effMax + temp;
    const pct     = total > 0 ? Math.floor((cur / total) * 100) : 0;
    const out: Record<string, unknown> = {
        v_current: cur, v_max: baseMax, effective_v_max: effMax,
        v_status: calcVStatus(cur, effMax, temp),
        v_percent: pct, v_bar_v_percent: pct, v_bar_total: total,
    };
    if (effMax - cur !== 0) out.v_lost = effMax - cur;
    if (temp !== 0) {
        const tPct = total > 0 ? Math.floor((temp / total) * 100) : 0;
        out.v_temp = temp; out.v_temp_percent = tPct; out.v_bar_v_temp_percent = tPct;
    }
    if (maxTemp !== 0) {
        out.v_max_temp = maxTemp;
        out.v_max_temp_percent = total > 0 ? Math.floor((maxTemp / total) * 100) : 0;
    }
    return { ...out, ...v }; // stored keys win
}

export function computedValue<T = string | number>(arr: any, bucket: string, key: string): T | undefined {
    if (arr === null || arr === undefined) return undefined;
    // Match the global reader: refused compilation must not turn saved health into 0/0.
    if (bucket === 'vitality' && arr._entity_refusal && !arr.computed?.creature && arr.encounter_creatures) {
        return deriveVitality(Object.fromEntries(['v_current', 'v_max', 'v_temp', 'v_max_temp']
            .map(field => [field, encounterCreatureField(arr.encounter_creatures, field) ?? 0])))[key] as T | undefined;
    }
    // v38 sparse inventory computed projection. Merge the instance override over the catalog
    // template before reading vitality; PL falls back directly to the catalog scalar.
    const invDef = _ftIsInventoryInstance(arr) ? _ftInventoryTemplateDef(arr) : null;
    if (invDef && bucket === 'vitality') {
        const defaults = invDef.computed?.vitality;
        const own = arr.computed?.vitality;
        if ((defaults && typeof defaults === 'object') || (own && typeof own === 'object')) {
            const merged = {
                ...(defaults && typeof defaults === 'object' ? defaults : {}),
                ...(own && typeof own === 'object' ? own : {}),
            };
            const value = deriveVitality(merged)[key];
            if (value !== undefined && value !== null) return value as T;
        }
    }
    if (invDef && bucket === 'pl' && (arr.computed?.pl === undefined || arr.computed?.pl === null)) {
        const value = invDef.computed?.pl;
        if (value !== undefined && value !== null) return (Array.isArray(value) ? value[0] : value) as T;
    }
    const bucketed = arr.computed != null && arr.computed[bucket] != null ? arr.computed[bucket][key] : undefined;
    if (bucketed !== undefined && bucketed !== null) {
        return (Array.isArray(bucketed) ? bucketed[0] : bucketed) as T;
    }
    if (invDef && (bucket === 'cost' || bucket === 'weight')) {
        const templateValue = invDef.computed?.[bucket]?.[key];
        if (templateValue !== undefined && templateValue !== null) {
            return (Array.isArray(templateValue) ? templateValue[0] : templateValue) as T;
        }
    }
    // Transitional: vitality was renamed from v_arr (2026-05-28). Cached blobs
    // may still carry computed.v_arr; fall back to that before legacy top-level.
    if (bucket === 'vitality' && arr.computed != null && arr.computed.v_arr != null) {
        const legacyComputed = arr.computed.v_arr[key];
        if (legacyComputed !== undefined && legacyComputed !== null) {
            return (Array.isArray(legacyComputed) ? legacyComputed[0] : legacyComputed) as T;
        }
    }
    let flatBucket: any = arr[bucket];
    if ((flatBucket === null || flatBucket === undefined) && bucket === 'vitality' && arr.v_arr != null) {
        flatBucket = arr.v_arr;
    }
    if (flatBucket === null || flatBucket === undefined) {
        // v33 (ROUND-12 #3): lean non-living vitality stores base state only — derive
        // the missing key from the canonical bucket (no-op for unknown keys).
        if (bucket === 'vitality' && arr.computed?.vitality != null) {
            const d = deriveVitality(arr.computed.vitality)[key];
            if (d !== undefined && d !== null) return d as T;
        }
        return undefined;
    }
    const flat = flatBucket[key];
    if (flat === undefined || flat === null) {
        if (bucket === 'vitality') {
            const d = deriveVitality(flatBucket)[key];
            if (d !== undefined && d !== null) return d as T;
        }
        return undefined;
    }
    return (Array.isArray(flat) ? flat[0] : flat) as T;
}


export function itemPictures(arr: any): any[] {
    // Legacy entry point — prefer picturesOf(arr, 'item') going forward.
    return picturesOf(arr, 'item');
}


// Stable per-node identity for the carrier-descent cycle guard below. A path keyed
// by item_id caps each descent at one visit per node, killing the unbounded recursion
// a self- or mutually-referencing default-provider carrier graph would otherwise blow
// the call stack with. Twin of PHP _ft_node_item_id() / item-arr-readers.ts _ftNodeItemId().
function _ftNodeItemId(arr: any): string | null {
    if (arr === null || typeof arr !== 'object') return null;
    let id = (arr.items && arr.items.item_id !== undefined) ? arr.items.item_id : arr.item_id;
    if (Array.isArray(id)) id = id.length ? id[0] : null;
    return (id === undefined || id === null || id === '') ? null : String(id);
}

/**
 * Bucket-aware picture list read. Sister to PHP pictures_of(). 2026-05-28 of
 * [[project_item_arr_bucket_reshape]] collapses item_pictures, encounter_creature_picture,
 * encounter_creature_presentation_picture, source_picture, etc. into a single `pictures`
 * bucket sub-keyed by attached_item_type. Fallback to legacy per-host buckets
 * for browser-cached blobs that pre-date the producer flip.
 */
export function picturesOf(arr: any, type: string | null = null, visited?: Record<string, boolean>): any[] {
    if (arr === null || arr === undefined) return [];
    // Cycle guard for the carrier recursion below: mark this node visited on the
    // current path. Mirror of PHP pictures_of($arr, $type, $visited).
    visited = visited || {};
    const selfId = _ftNodeItemId(arr);
    if (selfId !== null) visited[selfId] = true;
    const bucket = (arr.pictures != null && typeof arr.pictures === 'object') ? arr.pictures : null;
    if (type === null) {
        // Caller wants the whole bucket; return its values flattened.
        if (!bucket) return [];
        const out: any[] = [];
        for (const k of Object.keys(bucket)) {
            const v = (bucket as any)[k];
            if (Array.isArray(v)) {
                for (const row of v) out.push(row);
            }
        }
        return out;
    }
    if (bucket && Array.isArray((bucket as any)[type]) && (bucket as any)[type].length > 0) {
        return (bucket as any)[type];
    }
    const LEGACY: Record<string, string[]> = {
        item: ['item_pictures', 'item_picture'],
        encounter_creature_picture: ['encounter_creature_picture'],
        encounter_creature_presentation_picture: ['encounter_creature_presentation_picture'],
        encounter_creature_thumbnail_picture: ['encounter_creature_thumbnail_picture'],
        player_character_picture: ['player_character_picture'],
        inventory_picture: ['inventory_picture'],
        inventory_presentation_picture: ['inventory_presentation_picture'],
        inventory_thumbnail_picture: ['inventory_thumbnail_picture'],
        encounter_picture: ['encounter_picture'],
        location_picture: ['location_picture'],
        location_map: ['location_map'],
        source_header: ['source_picture'],
        source_header_picture: ['source_picture'],
    };
    for (const legacy of LEGACY[type] ?? []) {
        const v = arr[legacy];
        if (Array.isArray(v) && v.length > 0) return v;
    }
    // ROUND-4 thinned-instance bridge: the dropped picture sub-keys on an inventory instance —
    // the template gallery copy (`item`) resolves from the catalog def; the elided presentation/thumbnail
    // STAMPS re-derive from their pointer ids against the instance's own uploads or the def
    // gallery. Scoped to exactly these three types: `inventory_picture` (uploads) is
    // per-instance and never dropped. Real overrides keep their stamped rows and return above.
    if (
        (type === 'item' || type === 'inventory_presentation_picture' || type === 'inventory_thumbnail_picture')
        && _ftIsInventoryInstance(arr)
        && _ftInventoryTemplateId(arr) !== '' // ROUND-6 #1: items pointer bucket dropped — helper-gated
    ) {
        const resolvedDef = _ftInventoryTemplateDef(arr);
        // A catalog def is never itself an inventory instance; the resolver can hand back the row itself
        // (or another instance), and recursing into that loops forever. Twin of ftPicturesOf.
        const def = resolvedDef && resolvedDef !== arr && !_ftIsInventoryInstance(resolvedDef) ? resolvedDef : null;
        if (type === 'item') {
            if (def) {
                const defPics = picturesOf(def, 'item', visited);
                if (defPics.length > 0) return defPics;
            }
        } else {
            // ROUND-9 #1 follow-up: the instance's own KEPT gallery copy (pictures.item — the
            // all-enchanted-template case, where no def exists) is a resolution pool too, so
            // the pointer re-derives even when the catalog only carries an identity entry.
            // ROUND-9 sibling-stamp pool: the writer drops a stamp bucket whose rows the KEPT
            // sibling vouches (thumbnail usually duplicates the xz row exactly).
            const ptrCol = type === 'inventory_presentation_picture' ? 'inventory_presentation_pic_id' : 'inventory_thumbnail_pic_id';
            const siblingKey = type === 'inventory_presentation_picture' ? 'inventory_thumbnail_picture' : 'inventory_presentation_picture';
            const pid = inventoryField<string>(arr, ptrCol);
            if (typeof pid === 'string' && pid !== '') {
                const want = pid.toLowerCase();
                for (const pool of [arr.pictures?.inventory_picture, arr.pictures?.item, arr.pictures?.[siblingKey], def?.pictures?.item]) {
                    if (!Array.isArray(pool)) continue;
                    for (const row of pool) {
                        let rid = row?.picture_id;
                        if (Array.isArray(rid)) rid = rid[0];
                        if (typeof rid === 'string' && rid.toLowerCase() === want) return [row];
                    }
                }
                // v30: cross-item stamp art is interned as a catalog PICTURE entry —
                // {picture_url} keyed by the picture uuid; the pointer id is the reference.
                const pe = _ftCatalogIdentity(arr, pid);
                if (pe && typeof pe.picture_url === 'string' && pe.picture_url !== '') {
                    return [{ picture_id: pid, picture_url: pe.picture_url }];
                }
            }
        }
    }
    // ROUND-9 #9 EC-stamp bridge (twin of PHP pictures_of / JS ftPicturesOf): elided
    // encounter_creature presentation/thumbnail stamps re-derive from their pointer ids against the
    // member's own galleries (PC uploads / the creature item gallery). Member-root shape.
    const R9_EC_STAMPS: Record<string, string> = {
        encounter_creature_presentation_picture: 'encounter_creature_presentation_pic_id',
        encounter_creature_thumbnail_picture: 'encounter_creature_thumbnail_pic_id',
    };
    if (R9_EC_STAMPS[type] !== undefined && arr.encounter_creatures != null && typeof arr.encounter_creatures === 'object') {
        const ec = arr.encounter_creatures;
        const kept = (ec.pictures && typeof ec.pictures === 'object') ? ec.pictures[type] : null;
        if (Array.isArray(kept) && kept.length > 0) return kept;
        const ptrCol = R9_EC_STAMPS[type];
        let ptr = ec[ptrCol];
        if (Array.isArray(ptr)) ptr = ptr[0];
        if (typeof ptr === 'string' && ptr !== '') {
            const want = ptr.toLowerCase();
            for (const pool of [
                arr.player_characters?.pictures?.player_character_picture,
                bucket ? (bucket as any).item : null,
                (ec.pictures && typeof ec.pictures === 'object') ? ec.pictures.encounter_creature_picture : null,
            ]) {
                if (!Array.isArray(pool)) continue;
                for (const row of pool) {
                    let rid = row?.picture_id;
                    if (Array.isArray(rid)) rid = rid[0];
                    if (typeof rid === 'string' && rid.toLowerCase() === want) return [row];
                }
            }
        }
    }
    // [[project_classification_nest_and_walk]] P1 (2026-05-28): carrier walk.
    // When host has no pictures of $type, descend into each default-provider
    // carrier and return the first non-empty list. P1 is no-op until DB rows
    // flip in P2/P4.
    for (const carrier of _ftDefaultProviderCarriers(arr)) {
        const cid = _ftNodeItemId(carrier);
        if (cid !== null && visited[cid]) continue; // cycle — already visited on this path
        const carrierPics = picturesOf(carrier, type, visited);
        if (carrierPics.length > 0) return carrierPics;
    }
    return [];
}


/**
 * Returns the first picture row across a priority list of attached_item_types.
 * Classification carrier fallback is now handled INSIDE picturesOf via the
 * _ftDefaultProviderCarriers walker ([[project_classification_nest_and_walk]]
 * P1 2026-05-28), so priority only enumerates logical types — carriers no
 * longer appear here.
 */
// Shared picture-type fallback order — render_carousel's branch order (first non-empty type wins).
// primaryPictureOf and pictureListOf both walk this so the carousel and the single-image fallback
// always resolve from the SAME picture pool.
export const DEFAULT_PICTURE_TYPE_PRIORITY: string[] = [
    'encounter_creature_picture',
    'encounter_creature_presentation_picture',
    'encounter_creature_thumbnail_picture',
    'player_character_picture',
    'inventory_picture',
    'encounter_picture',
    'location_picture',
    'item',
];

export function primaryPictureOf(
    arr: any,
    typePriority: string[] = DEFAULT_PICTURE_TYPE_PRIORITY,
): any | null {
    if (arr === null || arr === undefined) return null;
    for (const type of typePriority) {
        const pics = picturesOf(arr, type);
        if (pics.length === 0) continue;
        for (const pic of pics) {
            if (pic && pic.primary_picture) return pic;
        }
        return pics[0] ?? null;
    }
    return null;
}

// Full picture LIST for the first matching type (carousel pool). Sibling of primaryPictureOf:
// primaryPictureOf returns one (primary-preferred) row; this returns every row of the winning type,
// in storage order — the carousel computes its own active index (creature thumbnail/xz pic).
export function pictureListOf(
    arr: any,
    typePriority: string[] = DEFAULT_PICTURE_TYPE_PRIORITY,
): any[] {
    if (arr === null || arr === undefined) return [];
    for (const type of typePriority) {
        const pics = picturesOf(arr, type);
        if (pics.length > 0) return pics;
    }
    return [];
}


export function itemVideos(arr: any): any[] {
    if (arr === null || arr === undefined) return [];
    const list = arr.item_videos != null ? arr.item_videos : arr.item_video;
    return Array.isArray(list) ? list : [];
}


/**
 * Bucket-aware single-valued layer read. Reads arr.item_layer_types[layer] then
 * arr[layer]; unwraps wrapped arrays and shells. When host has no value,
 * descends into default-provider carriers ([[project_classification_nest_and_walk]]
 * P1 2026-05-28) — first non-null wins (single-valued; aggregation_strategy
 * irrelevant here).
 */
export function layerOf<T = string>(arr: any, layerName: string, visited?: Record<string, boolean>): T | undefined {
    if (arr === null || arr === undefined) return undefined;
    // Cycle guard for the carrier recursion below — a self- or mutually-referencing
    // default-provider carrier graph would otherwise recurse without bound and blow
    // the call stack. Mirror of PHP layer_value($arr,$layer,$visited).
    visited = visited || {};
    const selfId = _ftNodeItemId(arr);
    if (selfId !== null) visited[selfId] = true;
    const presenceSlot = FT_CREATURE_PRESENCE_DEFENSE_SLOTS[layerName];
    const presenceRows = presenceSlot !== undefined
        ? arr?.computed?.creature?.defenses?.[presenceSlot]
        : undefined;
    if (Array.isArray(presenceRows) && presenceRows.length > 0) return 1 as T;
    const bucket = arr.item_layer_types != null ? arr.item_layer_types[layerName] : undefined;
    const slot = bucket !== undefined ? bucket : arr[layerName];
    const hostValue = layerValue<T>(slot);
    if (hostValue !== undefined && hostValue !== null) return hostValue;
    // ROUND-4 thinned-instance bridge: an inventory instance whose item_layer_types was dropped
    // (byte-identical to the catalog template def) reads from the def — before the carrier walk,
    // since the def's own layerOf recursion covers its carriers.
    if (_ftIsThinnedInventoryInstance(arr)) {
        const def = _ftInventoryTemplateDef(arr);
        if (def) {
            const defValue = layerOf<T>(def, layerName, visited);
            if (defValue !== undefined && defValue !== null) return defValue;
        }
    }
    for (const carrier of _ftDefaultProviderCarriers(arr)) {
        const cid = _ftNodeItemId(carrier);
        if (cid !== null && visited[cid]) continue; // cycle — already visited on this path
        const carrierValue = layerOf<T>(carrier, layerName, visited);
        if (carrierValue !== undefined && carrierValue !== null) return carrierValue;
    }
    return undefined;
}


/** Raw bucket slot read for multi-valued layer iteration. Does NOT walk carriers — use layerEntriesOf for aggregation. */
export function layerSlot(arr: any, layerName: string): any {
    if (arr === null || arr === undefined) return undefined;
    const bucket = arr.item_layer_types != null ? arr.item_layer_types[layerName] : undefined;
    if (bucket !== undefined) return bucket;
    // computed.creature.defenses bridge — mirror of PHP _ft_read_layer_slot_raw / JS ftLayerSlot.
    // Sits AFTER pristine item_layer_types, BEFORE the legacy top-level fallback. (Was missing in TS.)
    const defSlot = FT_CREATURE_DEFENSE_SLOTS[layerName];
    if (defSlot !== undefined
        && arr.computed && typeof arr.computed === 'object'
        && arr.computed.creature && typeof arr.computed.creature === 'object'
        && arr.computed.creature.defenses && typeof arr.computed.creature.defenses === 'object'
        && Array.isArray(arr.computed.creature.defenses[defSlot])
        && arr.computed.creature.defenses[defSlot].length > 0
    ) {
        return arr.computed.creature.defenses[defSlot];
    }
    const presenceDefSlot = FT_CREATURE_PRESENCE_DEFENSE_SLOTS[layerName];
    if (presenceDefSlot !== undefined
        && arr.computed && typeof arr.computed === 'object'
        && arr.computed.creature && typeof arr.computed.creature === 'object'
        && arr.computed.creature.defenses && typeof arr.computed.creature.defenses === 'object'
        && Array.isArray(arr.computed.creature.defenses[presenceDefSlot])
        && arr.computed.creature.defenses[presenceDefSlot].length > 0
    ) {
        return arr.computed.creature.defenses[presenceDefSlot];
    }
    return arr[layerName];
}


/**
 * Multi-valued layer read with carrier walk and aggregation strategy.
 * Mirror of PHP `layer_entries`. Returns a list of entries; respects per-layer
 * AGGREGATION_STRATEGY (host_wins / prepend / append) for combining host +
 * carrier entries when both exist.
 *
 * Usage: `for (const entry of layerEntriesOf(creature, 'attack_ability')) { … }`
 */
export function layerEntriesOf(arr: any, layerName: string, visited?: Record<string, boolean>): any[] {
    if (arr === null || arr === undefined) return [];
    // Cycle guard for the carrier recursion below — see layerOf().
    // Mirror of PHP layer_entries($arr,$layer,$visited).
    visited = visited || {};
    const selfId = _ftNodeItemId(arr);
    if (selfId !== null) visited[selfId] = true;
    const raw = layerSlot(arr, layerName);
    const hostEntries = _ftNormalizeLayerEntries(raw).filter(entry => layerName !== 'status_effect_permanent'
        || permanentStatusAllowed(layerListEntryUuid(entry)));
    // ROUND-4 thinned-instance bridge: delegate the whole read to the catalog template def —
    // reproduces pre-drop semantics exactly (host entries + the def's carrier aggregation).
    // Only when the host (incl. legacy flat keys) had nothing, mirroring PHP layer_entries().
    if (hostEntries.length === 0 && _ftIsThinnedInventoryInstance(arr)) {
        const def = _ftInventoryTemplateDef(arr);
        if (def) return layerEntriesOf(def, layerName, visited);
    }
    const strategy = _ftAggregationStrategy(layerName);
    if (strategy === 'host_wins') {
        if (hostEntries.length > 0) return hostEntries;
        for (const carrier of _ftDefaultProviderCarriers(arr)) {
            const cid = _ftNodeItemId(carrier);
            if (cid !== null && visited[cid]) continue; // cycle — already visited on this path
            const carrierEntries = layerEntriesOf(carrier, layerName, visited);
            if (carrierEntries.length > 0) return carrierEntries;
        }
        return [];
    }
    // prepend / append: aggregate with dedupe by UUID.
    // DEFENSE-AWARE UID (mirror of PHP build_item_arr.php + item-arr-readers.js): structured
    // damage-defense rows baked into computed.creature.defenses key the UUID under 'damage_type',
    // which _ftLayerListEntryUuid does NOT read — so a carrier defense materialized by
    // compute_defenses must dedupe against the read-time carrier re-walk by damage_type, else it doubles.
    const _isDefSlot = FT_CREATURE_DEFENSE_SLOTS[layerName] !== undefined;
    const _entryUid = (e: any): string => {
        if (_isDefSlot && e !== null && typeof e === 'object' && typeof e.damage_type === 'string') return e.damage_type;
        return _ftLayerListEntryUuid(e);
    };
    const seen = new Set<string>();
    const result: any[] = [];
    for (const entry of hostEntries) {
        const uid = _entryUid(entry);
        if (uid !== '' && seen.has(uid)) continue;
        if (uid !== '') seen.add(uid);
        result.push(entry);
    }
    for (const carrier of _ftDefaultProviderCarriers(arr)) {
        const cid = _ftNodeItemId(carrier);
        if (cid !== null && visited[cid]) continue; // cycle — already visited on this path
        const carrierEntries = layerEntriesOf(carrier, layerName, visited);
        for (const entry of carrierEntries) {
            const uid = _entryUid(entry);
            if (uid !== '' && seen.has(uid)) continue;
            if (uid !== '') seen.add(uid);
            if (strategy === 'prepend') {
                result.unshift(entry);
            } else {
                result.push(entry);
            }
        }
    }
    return result;
}

/**
 * TS twin of PHP `layer_has()` / JS `ftLayerHas`. True if the layer resolves to a
 * value OR has entries. A value of 0 / '' / false still counts (matches PHP `!== null`).
 */
export function layerHas(arr: any, layerName: string): boolean {
    if (arr === null || arr === undefined) return false;
    const v = layerOf(arr, layerName);
    if (v !== undefined && v !== null) return true;
    return layerEntriesOf(arr, layerName).length > 0;
}

/** Public UUID extractor for a layer list entry — mirror of PHP `layer_list_entry_uuid`. */
export function layerListEntryUuid(entry: any): string {
    return _ftLayerListEntryUuid(entry);
}

/**
 * TS twin of PHP `item_arr_scalar_layer_slot()` / JS `ftScalarLayerSlot`. A layer cell
 * as a single display string for iconMap / openItemInfo / HTML attrs — UUID from a
 * structured row, else scoring token, else scalar. The arg is the SLOT VALUE.
 */
export function scalarLayerSlot(slot: any): string {
    if (slot === null || slot === '' || slot === undefined) return '';
    if (typeof slot === 'object') {
        const tok = _ftScoringTokenFromEntry(slot);
        if (tok !== null && _FT_UUID_RE.test(tok.trim())) return tok;
        const u = _ftLayerListEntryUuid(slot);
        if (u !== '' && _FT_UUID_RE.test(u.trim())) return u;
        return tok !== null ? tok : '';
    }
    if (typeof slot === 'string' || typeof slot === 'number') {
        return String(slot);
    }
    return '';
}

const _FT_UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Scoring token of a layer cell — mirror of PHP `layer_scoring_token_from_entry`. */
function _ftScoringTokenFromEntry(value: any): string | null {
    if (typeof value === 'string' || typeof value === 'number') {
        const s = String(value).trim();
        return s === '' ? null : s;
    }
    if (value !== null && typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'item_layer_effect')) {
            const e = String(value.item_layer_effect != null ? value.item_layer_effect : '').trim();
            return e === '' ? null : e;
        }
        if (typeof value[0] === 'string' || typeof value[0] === 'number') {
            const z = String(value[0]).trim();
            return z === '' ? null : z;
        }
    }
    return null;
}

/** TS twin of PHP `source_of()` / JS `ftSourceOf`. The source bucket, or null. */
export function sourceOf(arr: any): any {
    if (arr === null || arr === undefined) return null;
    if (arr.items != null && arr.items.source != null && typeof arr.items.source === 'object') {
        return arr.items.source;
    }
    if (arr.source != null && typeof arr.source === 'object') {
        return arr.source;
    }
    return null;
}

function _ftNormalizeLayerEntries(value: any): any[] {
    if (value === null || value === undefined || value === '') return [];
    if (Array.isArray(value)) {
        // Wrapped list (legacy or multi-layer) vs rich entry / shell at top.
        // A real list has integer index 0; a rich/shell entry has string keys.
        // Bucketed shell ({items:…}) and full-nested arr counted as single entry.
        if (value.length === 0) return [];
        return value;
    }
    if (typeof value === 'object') {
        return [value];
    }
    return [value];
}


// Encounter reshape (2026-06-09): build_encounter_arr.php now BUCKETS the encounters-table own-row
// columns under the `encounters` source-table bucket (bare scalars), mirroring items/locations/
// campaigns. Read every own-row column via encounterField() -- it is bucket-first and still unwraps
// the legacy flat top-level / [0] cached-blob shapes during rollover. The flat PhpScalar fields below
// are retained ONLY as a union-tolerant hint for that rollover window; do NOT add new direct or
// `?.[0]` reads -- always go through encounterField(). DERIVED / entity keys (encounter_zone,
// encounter_range_*, encounter_*_map, pictures, encounter_creatures_arr, encounter_inventories_arr,
// statuses) stay TOP-LEVEL and are NOT in the bucket.
export interface EncounterArr {
    // Source-table bucket: the encounters-table own-row columns (encounter_id, encounter_name,
    // encounter_location_id, in_combat, current_round, encounter_light/weather/environment, ...).
    encounters?: { [key: string]: unknown };

    encounter_id: PhpScalar<string>;
    encounter_name?: PhpScalar<string>;
    encounter_master_parent_id?: PhpScalar<string>;
    current_encounter_creature_id_turn?: PhpScalar<string>;
    in_combat?: PhpScalar<string | number>;
    current_round?: PhpScalar<string | number>;

    // ─── Geometry / location ──────────────────────────────────────────────
    encounter_max_x?: PhpScalar<string | number>;
    encounter_max_z?: PhpScalar<string | number>;
    encounter_explored?: PhpScalar<string | number>;
    encounter_location_id?: PhpScalar<string>;
    encounter_location_map?: PhpScalar<string>;
    encounter_location_parent_id?: PhpScalar<string>;
    encounter_location_parent_name?: PhpScalar<string>;
    location_parent_map?: PhpScalar<string>;
    encounter_zone?: PhpScalar<string>;

    // ─── Visual / environment ─────────────────────────────────────────────
    // encounter_picture is a list of `pictures` rows attached to the encounter
    // (attached_item_type='encounter_picture'). Picture row fields are descalared
    // per [[feedback_table_of_origin_shape]] — `pic_url` is a bare string, not [0]-wrapped.
    encounter_picture?: PictureRow[];
    encounter_environment?: PhpScalar<string>;
    encounter_weather?: PhpScalar<string>;
    // Weather axes (2026-08-14, WEATHER_AXES_PLAN.md): band UUIDs shell like weather; direction /
    // water level are plain scalars; terrain strings are generator-only variable_names.
    encounter_tempurature?: PhpScalar<string>;
    encounter_wind?: PhpScalar<string>;
    encounter_wind_direction?: PhpScalar<string | number>;
    encounter_topography?: PhpScalar<string>;
    encounter_vegetation_type?: PhpScalar<string>;
    encounter_vegetation_density?: PhpScalar<string>;
    encounter_water_level?: PhpScalar<string | number>;
    encounter_light?: PhpScalar<string>;
    encounter_source_light?: PhpScalar<string>;

    // ─── Text content (also gate the GM-notes/ambience divider) ───────────
    encounter_description?: PhpScalar<string>;
    encounter_gm_notes?: PhpScalar<string>;
    encounter_mx?: PhpScalar<string>;

    // ─── Audio ───────────────────────────────────────────────────────────
    encounter_amb?: PhpScalar<string>;
    encounter_amb_night?: PhpScalar<string>;
    encounter_amb_weather?: PhpScalar<string>;

    // ─── Subordinate row collections (Phase 3a/b additions) ──────────────
    // These come through as arrays after `normalizePhpListish()` coercion.
    // The encounter-map bundle reads them in dozens of places.
    encounter_creatures_arr?: EncounterCreatureRow[];
    encounter_inventories_arr?: EncounterInventoryRow[];
    // Statuses applied to (target) / maintained by (caster) this carrier.
    // Read via the ftCreatureStatuses() global, not directly. See creature_statuses.php.
    statuses?: { target?: StatusEffectInstance[]; caster?: StatusEffectInstance[] };

    // get_encounter_arr.php returns ~80 fields; carve-outs only read a subset.
    [key: string]: unknown;
}


export type CampaignFt3dMode = 'active' | 'paused' | 'theatre';

export interface CampaignArr {
    // Campaign reshape (2026-06-04): the campaigns-table row + SQL-joined columns live here as
    // BARE scalars (no PhpScalar [0] wrap). Read via the bucket-first helpers (e.g. _ftCamField).
    campaigns?: { campaign_ft3d_mode?: CampaignFt3dMode; [key: string]: unknown };
    // Genuinely-computed/derived values (campaign_level, current_session_number, session_price_f,
    // calendar_current_*_f) — bare scalars under computed.campaign.
    computed?: { campaign?: { [key: string]: unknown } };

    campaign_id: PhpScalar<string>;

    // ─── Permissions (GM-only UI gates) ───────────────────────────────────
    campaign_permission?: PhpScalar<string | number>;

    // ─── Turn timer ───────────────────────────────────────────────────────
    default_creature_turn_timer?: PhpScalar<string | number>;

    // ─── Toggles + interlocks ─────────────────────────────────────────────
    campaign_disable_player_haven_return?: PhpScalar<string | number>;
    campaign_downtime_enabled?: PhpScalar<string | number>;
    // Legacy top-level fallback only; canonical payloads carry this in campaigns.campaign_ft3d_mode.
    campaign_ft3d_mode?: PhpScalar<CampaignFt3dMode>;
    hunger_thirst_enabled?: PhpScalar<string | number>;
    waking_weariness_enabled?: PhpScalar<string | number>;

    // ─── Calendar / environment (the summary widget reads these) ──────────
    campaign_sun_state?: PhpScalar<string>;
    month_season?: PhpScalar<string>;
    calendar_id?: PhpScalar<string>;
    calendar_current_date_f?: PhpScalar<string>;
    calendar_current_time_f?: PhpScalar<string>;
    calendar_current_full_date_named_f?: PhpScalar<string>;

    // ─── GM navigation / audio (read by the WS campaign-update handler) ───
    encounter_loaded?: PhpScalar<string>;
    // campaign_encounter_amb removed (2026-06-30): ambience is now scoped per-encounter (see
    // ftSyncViewerAmbience / encounter_amb* slots); the campaign-global pointer + column were dropped.
    campaign_encounter_mx?: PhpScalar<string>;

    [key: string]: unknown;
}


// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-interfaces used by the encounter-map carve-out (Phase 3a).
// Added when the bundle's controllers needed to drop `: any` for PHP-blob
// references. Each follows the same `PhpScalar<T>` + catch-all-indexer pattern.
// ─────────────────────────────────────────────────────────────────────────────

/** `{ x_pos, y_pos, z_pos }` — the grid-position sub-object on creatures / inventory / caster.
 *  Renamed from `pos_arr` to `position` and descalared to bare scalars 2026-06-02; coord
 *  fields stay union-typed to tolerate cached `[0]`-wrapped blobs during deploy rollover.
 *  Read coordinates via posCoord(). `PosArr` remains as a deprecated alias. */
export interface Position {
    x_pos?: number | string | PhpScalar<string | number>;
    y_pos?: number | string | PhpScalar<string | number>;
    z_pos?: number | string | PhpScalar<string | number>;
    [key: string]: unknown;
}
/** @deprecated Renamed to {@link Position}. */
export type PosArr = Position;


/**
 * `{ picture_url, picture_width, picture_height }` — what `getBestPicture()` returns.
 * Phase 2.5d (2026-05-23): fields descalared per the table-of-origin rule
 * ([[feedback_table_of_origin_shape]]). build_picture_arr emits direct scalars now;
 * `pictureField0()` ambient helper stays shape-tolerant as defense for code paths
 * that bypass build_picture_arr or for older cached blobs during the Redis warm-up
 * after deploy. EncounterCreatureRow.pic_url / EncounterInventoryRow.pic_url stay
 * PhpScalar — those are inventory/creature-level denormalized fields, NOT
 * picture-bucket fields, and Phase 2.5 scope doesn't touch them.
 */
export interface PictureField {
    // picture_url is the canonical url key (renamed from pic_url 2026-06-02 for
    // *_url naming parity). Read via pictureField0(), which also accepts the legacy
    // 'pic_url' key alias and returns picture_url.
    picture_url?: string;
    picture_width?: string | number;
    picture_height?: string | number;
    [key: string]: unknown;
}


/**
 * A row from the `pictures` table as emitted by build_picture_arr.php.
 * Fields are descalared per [[feedback_table_of_origin_shape]] — `pic_url`,
 * `attached_item_type`, etc. are bare scalars (not [0]-wrapped). Used as
 * the element type of buckets like `encounter_picture[]` on EncounterArr.
 */
export interface PictureRow {
    // Canonical url key (renamed from pic_url 2026-06-02). Read via pictureField0(),
    // which also accepts the legacy 'pic_url' key alias and returns picture_url.
    picture_url?: string;
    picture_id?: string;
    primary_picture?: string | number;
    picture_thumbnail?: string | number;
    picture_attached_item_type?: string;
    picture_reference_attached_item_type?: string;
    picture_attached_id?: string;
    [key: string]: unknown;
}


/**
 * `v_arr` — vitality bar percentages on creatures and inventory items.
 * Drives the HP / temp HP / armor / special bars animated by the
 * creature-render bundle's animateEntityChanges.
 */
export interface VBarArr {
    v_bar_v_percent?: PhpScalar<string | number>;
    v_bar_v_temp_percent?: PhpScalar<string | number>;
    v_bar_v_armor_percent?: PhpScalar<string | number>;
    v_bar_v_temp_armor_percent?: PhpScalar<string | number>;
    v_bar_v_special_percent?: PhpScalar<string | number>;
    [key: string]: unknown;
}


/**
 * A status row. Read it via the `ftCreatureStatuses(carrier, mode)` global
 * (mirror of PHP creature_statuses): it returns `carrier.statuses.target` /
 * `carrier.statuses.caster`. Entries are lean — zero/empty fields are omitted
 * and item_type rides as a bare scalar — so read fields defensively (`?? `,
 * optional chaining). The persistent-AOE renderer + XZVisibility read these heavily.
 */
export interface StatusEffectInstance {
    status_id?: PhpScalar<string>;
    item_id?: PhpScalar<string>;
    item_name?: PhpScalar<string>;
    status_private?: PhpScalar<string | number>;
    /** ROUND-9 #11: cached rows carry the collapsed def REF here; ftCreatureStatuses
     *  presents the resolved def under status_arr on the returned copy. */
    status_item_id?: string;
    status_arr?: { [key: string]: unknown };

    // Persistent-AOE geometry (origin + distance points define the placed shape).
    status_origin_point_x?: PhpScalar<string | number>;
    status_origin_point_z?: PhpScalar<string | number>;
    status_distance_point_x?: PhpScalar<string | number>;
    status_distance_point_z?: PhpScalar<string | number>;
    status_aoe_distance?: PhpScalar<string | number>;
    status_aoe_distance_height?: PhpScalar<string | number>;
    status_aoe_distance_depth?: PhpScalar<string | number>;
    status_aoe_type?: PhpScalar<string>;
    status_aoe_source?: PhpScalar<string | number>;
    status_aoe_geometry?: string | null;

    [key: string]: unknown;
}


/**
 * The `encounter_creatures` sub-object on each creature row.
 * Carries the identity + initiative + speed metadata that flows through the
 * combat widget + turn controls + drag movement-mode resolution.
 */
// encounter_creatures reshape (2026-06-05): build_encounter_creature_arr.php is a PURE-ROW
// table -- these inner fields are now BARE scalars (no [0] wrap). Read via
// encounterCreatureField(entity.encounter_creatures, '<field>'); do NOT add new `?.[0]` reads.
export interface EncounterCreatureInner {
    encounter_creature_id?: PhpScalar<string>;
    encounter_id?: PhpScalar<string>;
    creature_initiative?: PhpScalar<string | number>;
    creature_number?: PhpScalar<string | number>;
    preferred_speed_type?: PhpScalar<string>;

    // Read by the creature-render bundle's animateEntityChanges:
    creature_name?: PhpScalar<string>;
    mana_percent?: PhpScalar<string | number>;
    aps?: PhpScalar<string | number>;
    reaction?: PhpScalar<string | number>;
    is_dead?: PhpScalar<string | number>;

    [key: string]: unknown;
}


/** A row in `encounterArr.encounter_creatures_arr[]` (one creature on the map). */
export interface EncounterCreatureRow {
    encounter_creatures?: EncounterCreatureInner;
    position?: Position;
    v_arr?: VBarArr;
    // Statuses applied to (target) / maintained by (caster) this carrier.
    // Read via the ftCreatureStatuses() global, not directly. See creature_statuses.php.
    statuses?: { target?: StatusEffectInstance[]; caster?: StatusEffectInstance[] };
    status_effect_permanent?: string[];
    player_characters?: { [key: string]: unknown };

    size?: PhpScalar<string>;
    width_ft?: PhpScalar<string | number>;
    height_ft?: PhpScalar<string | number>;
    reach?: PhpScalar<string | number>;
    pic_url?: PhpScalar<string>;

    // Light / vision modifiers carried per-creature.
    bright_light_dim_light?: PhpScalar<string | number>;
    magical_light?: PhpScalar<string | number>;
    magical_darkness?: PhpScalar<string | number>;

    [key: string]: unknown;
}


/** A row in `encounterArr.encounter_inventories_arr[]` (one inventory token on the map). */
export interface EncounterInventoryRow {
    inventory_id?: PhpScalar<string>;
    possess_id?: PhpScalar<string>;
    position?: Position;
    v_arr?: VBarArr;
    // Statuses applied to (target) / maintained by (caster) this carrier.
    // Read via the ftCreatureStatuses() global, not directly. See creature_statuses.php.
    statuses?: { target?: StatusEffectInstance[]; caster?: StatusEffectInstance[] };
    status_effect_permanent?: string[];

    width_ft?: PhpScalar<string | number>;
    height_ft?: PhpScalar<string | number>;
    pic_url?: PhpScalar<string>;
    primary_pic_url?: PhpScalar<string>;

    // Read by the creature-render bundle's animateEntityChanges + animateStatusEffectChanges:
    inventory_name?: PhpScalar<string>;
    item_name?: PhpScalar<string>;
    item_qty?: PhpScalar<string | number>;

    // Light / vision modifiers carried per-inventory item.
    bright_light_dim_light?: PhpScalar<string | number>;
    magical_light?: PhpScalar<string | number>;
    magical_darkness?: PhpScalar<string | number>;

    [key: string]: unknown;
}


/**
 * Union of either kind of "entity" that flows through the creature-render
 * bundle's `animateEntityChanges` / `animateStatusEffectChanges`. The runtime
 * branches on `entity.encounter_creatures?.encounter_creature_id?.[0]`
 * vs `entity.inventory_id?.[0]` to discriminate.
 */
export type EntityRow = EncounterCreatureRow | EncounterInventoryRow;


/**
 * The sidelog-active caster shape (window.FateTide.casterCache values).
 * Carries the identity + position + sensing + permissions that flow through
 * XZVisibility's can-see / can-target / reach checks.
 */
export interface CasterArr {
    encounter_creatures?: EncounterCreatureInner & {
        // Caster sheets carry the encounter binding here (get_caster_arr.php
        // attaches it on first load; the WS caster_update path keeps it
        // synced). Turn controls use this linkback.
        encounter_id?: PhpScalar<string>;
    };

    position?: Position;

    // Senses (all numeric ft; 0 = absent). Heavy use in XZVisibility.canSeeWithSenses.
    blindsight?: PhpScalar<string | number>;
    blindsight_blind?: PhpScalar<string | number>;
    fiendsight?: PhpScalar<string | number>;
    ghostsight?: PhpScalar<string | number>;
    nightsight?: PhpScalar<string | number>;
    spellsight?: PhpScalar<string | number>;
    tremorsense?: PhpScalar<string | number>;
    truesight?: PhpScalar<string | number>;

    reach?: PhpScalar<string | number>;

    // Per-caster light radius (rare; some classes radiate light themselves).
    bright_light_dim_light?: PhpScalar<string | number>;
    magical_light?: PhpScalar<string | number>;
    magical_darkness?: PhpScalar<string | number>;

    // Statuses applied to (target) / maintained by (caster) this carrier.
    // Read via the ftCreatureStatuses() global, not directly. See creature_statuses.php.
    statuses?: { target?: StatusEffectInstance[]; caster?: StatusEffectInstance[] };

    // [[project_classification_nest_and_walk]] Phase 5A.1 round 5 (2026-05-29):
    // Permissions live at $arr['permissions'] (boolean). The legacy top-level
    // $arr['player_permission'] / $arr['campaign_permission'][0] (string '1'/'0')
    // remain during the consumer migration window — dual-written by PHP.
    // Use the `hasPlayerPermission` / `hasCampaignPermission` helpers below
    // rather than touching either field directly; helpers prefer the new bucket
    // and fall back to the legacy shape.
    permissions?: { player?: boolean; campaign?: boolean };
    campaign_permission?: PhpScalar<string | number>;
    player_permission?: PhpScalar<string | number>;

    // Wire-shape version stamped by the PHP serializer (ft_object_api_finalize_for_client →
    // FT_OBJECT_SHAPE_V). Compared against EXPECTED_OBJECT_SHAPE_V on cache fill to detect a blob
    // built under an outdated $item_arr shape. See [[project_object_api_serializer]].
    _meta?: { shape_v?: number };

    // Phase 5 root shell catalog: uuid -> {item_name,item_type}. Interned by
    // ft_intern_shell_catalog() on every finalized blob; the identity source for
    // shells/refs once stage 3 collapses duplicate nests. Read via
    // catalogShell(root, uuid). See [[project_data_shape_leanup_master]].
    catalog?: Record<string, { item_name?: string; item_type?: string }>;

    [key: string]: unknown;
}

/**
 * Minimal generic wire shape returned by options/api/object_inc.php (hydrate → redact → stamp) for ANY
 * object_type. The per-object_type renderers (Step 3+) narrow this further; for now it just exposes the
 * identity + permission booleans + the _meta shape-version tripwire it shares with CasterArr. Anything
 * else is reachable through the index signature.
 */
export interface ObjectArr {
    items?: Record<string, unknown>;
    permissions?: { player?: boolean; campaign?: boolean };
    _meta?: { shape_v?: number };
    [key: string]: unknown;
}

/**
 * Front-end half of the object wire-shape tripwire. MUST be bumped in lockstep with PHP
 * FT_OBJECT_SHAPE_V (includes/functions/api/ft_object_api_inc.php) whenever the caster/object wire
 * shape changes — they are the two ends of the same contract.
 *
 * ROUND-9 follow-up (2026-06-12): this sat at 1 while PHP marched to 29, so ftWarnIfShapeStale
 * warned on EVERY cache fill for ~28 versions — console noise, and a tripwire that always fires
 * catches nothing. Re-synced to 29; keep it in the same commit as any future PHP bump (the
 * define() site carries the matching reminder).
 */
export const EXPECTED_OBJECT_SHAPE_V = 40;

/**
 * WARN-ONLY tripwire: log when a freshly-filled caster blob's wire-shape version differs from what
 * this bundle understands (a stale blob, or PHP/JS deployed out of lockstep). Takes NO action yet —
 * the eventual active behavior is a one-time hard reload, not a refetch (refetch would loop a stale
 * tab against a newer server). Called at every casterCache fill site.
 */
export function ftWarnIfShapeStale(
    arr: { _meta?: { shape_v?: number } } | null | undefined,
    ctx: string,
): void {
    const v = arr?._meta?.shape_v;
    if (v !== EXPECTED_OBJECT_SHAPE_V) {
        const page = globalThis as typeof globalThis & { __ftShapeWarnings?: Set<string> };
        const warned = page.__ftShapeWarnings ??= new Set<string>();
        if (warned.has(ctx)) return;
        warned.add(ctx);
        console.warn(
            `[shape_v] ${ctx}: caster blob shape_v=${String(v)} != expected ${EXPECTED_OBJECT_SHAPE_V} (warn-only)`,
        );
    }
}

/**
 * Boolean read of the viewer-grant on a creature / EC / inventory arr.
 * Prefers `arr.permissions.player` (new canonical bucket) and falls back
 * to the legacy `arr.player_permission` (string or number) during the
 * consumer migration window. Sister to PHP `has_player_permission()`.
 */
export function hasPlayerPermission(arr: any): boolean {
    if (arr === null || arr === undefined) return false;
    return arr.permissions?.player === true;
}

/**
 * Boolean read of the campaign-level (GM) grant. Sister to PHP
 * `has_campaign_permission()`.
 */
export function hasCampaignPermission(arr: any): boolean {
    if (arr === null || arr === undefined) return false;
    return arr.permissions?.campaign === true;
}

// NOTE (2026-06-17): unified-viewer-scoping Chunk 2 was re-attempted with DESIGN B (owner-ruled):
// the broadcast wire is made viewer-AGNOSTIC by STRIPPING the actor-stamped creature `permissions`
// server-side (ft_object_api_strip_actor_creature_permissions), and the client PRESERVES each
// viewer's seed-correct value by carrying the prior cached row's `permissions` forward across
// whole-row swaps (websocket/caster-cache.ts ftCarryPermissionsForward + heal-on-access). There is
// NO client-side re-derivation — the earlier Design A (viewerHas* helpers) was discarded because the
// fields it needs (permissioned / *_created_by_id / controller id) are lean-elided off the wire.
// Readers below (hasPlayerPermission / hasCampaignPermission) are UNCHANGED — they read the
// preserved per-viewer `permissions`. See plans/unified-viewer-scoping.md + OPUS_REVIEW_QUEUE.md.
